import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, LoadScriptNext, Marker } from "@react-google-maps/api";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styles from "./campaigns.page.module.scss";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import env from "environments/env";
import { Path } from "enums";
import {
  getTotalProcessedResponses,
  getCategoryDistribution,
  getSentimentDistribution,
  getEmploymentSectorDistribution,
  getEnumeratorPerformance,
  getKeyConcerns,
  getAgeDistribution,
  getSexDistribution,
} from "apis/survey-analytics.api";
import {
  getFieldResponsesProcessed,
  deleteFieldResponseProcessed,
} from "apis/field-response-processed.api";
import {
  getCampaigns as getCampaignsRequest,
  createCampaign as createCampaignRequest,
  endCampaign as endCampaignRequest,
  getCampaignByCode as getCampaignByCodeRequest,
  getCampaignQuestions as getCampaignQuestionsRequest,
} from "apis/campaign.api";
import {
  createChatThread,
  getChatThreads,
  getChatThreadMessages,
  createChatThreadMessage,
} from "apis/chat.api";

const RECORDINGS_API_PER_PAGE = 10000;
const RECORDINGS_UI_PER_PAGE = 5;

const statCards = [
  { label: "Total Campaigns", value: "3", tone: "default" },
  { label: "Active", value: "3", tone: "active" },
  { label: "Total Recordings", value: "0", tone: "info" },
  { label: "Enumerators", value: "0", tone: "accent" },
];

const EMPTY_CAMPAIGN_DETAILS = {
  totalRecordings: 0,
  enumeratorPerformance: [],
  ageDistribution: [],
  genderDistribution: [],
  recordings: [],
};

const formatCampaignDate = (value) => {
  if (!value) return "-";
  const asString = String(value);
  return asString.includes("T") ? asString.split("T")[0] : asString;
};

const formatExportDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null;

  const pad = (part) => String(part).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(
    parsed.getHours(),
  )}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
};

const toCampaignDateISO = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map((part) => Number(part));
  return new Date(Date.UTC(year, month - 1, day)).toISOString();
};

const toAgeNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value.replace(/[^\d]/g, ""), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const toAgeGroupFromAge = (value) => {
  const age = toAgeNumber(value);
  if (age == null || age <= 0) return "";
  if (age <= 17) return "17 and below";
  if (age <= 24) return "18-24";
  if (age <= 34) return "25-34";
  if (age <= 44) return "35-44";
  if (age <= 54) return "45-54";
  if (age <= 64) return "55-64";
  return "65+";
};

const AGE_DISTRIBUTION_BUCKETS = ["0-17", "18-29", "30-44", "45-59", "60+", "Unknown"];

const resolveAgeDistributionBucket = (value) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return "Unknown";

  if (/(unknown|n\/a|na|not\s+specified|prefer\s+not|decline)/.test(raw)) {
    return "Unknown";
  }

  const values = raw.match(/\d+/g) || [];
  const numbers = values.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry));

  if (numbers.length === 0) {
    return "Unknown";
  }

  let representativeAge;
  if (numbers.length >= 2) {
    representativeAge = (numbers[0] + numbers[1]) / 2;
  } else {
    representativeAge = numbers[0];
  }

  if (representativeAge <= 17) return "0-17";
  if (representativeAge <= 29) return "18-29";
  if (representativeAge <= 44) return "30-44";
  if (representativeAge <= 59) return "45-59";
  return "60+";
};

const normalizeCampaign = (campaign) => {
  const code = campaign?.campaignCode || campaign?.code || "";
  const rawSurveyType = String(campaign?.surveyType || campaign?.type || "single").toLowerCase();
  const surveyType = rawSurveyType === "custom" ? "custom" : "single";
  return {
    campaignId: campaign?.campaignId,
    code,
    status: String(campaign?.status || "inactive").toLowerCase(),
    title: campaign?.description || campaign?.name || code,
    date: formatCampaignDate(campaign?.campaignDate || campaign?.date),
    location: campaign?.location || "Unknown",
    surveyType,
    surveyQuestionDocument: campaign?.surveyQuestionDocument || "",
    enumerators: 0,
    recordingsSummary: "0 total",
    pos: 0,
    neg: 0,
    neutral: 0,
    topConcern: "—",
    avgSentiment: 0,
    details: EMPTY_CAMPAIGN_DETAILS,
  };
};

const sentimentColor = (s) => {
  if (s === "positive") return "#34d399";
  if (s === "negative") return "#f87171";
  return "#fbbf24";
};

const SURVEY_TYPE_OPTIONS = [
  {
    value: "single",
    label: "Simple Survey",
    description:
      "Free-form audio interview. Optionally attach a survey guide document for enumerators.",
  },
  {
    value: "custom",
    label: "Custom Survey",
    description:
      "Build a guided question flow, branching logic, and answer routing for Nexus enumerators.",
  },
];

const getSurveyTypeLabel = (surveyType) =>
  surveyType === "custom" ? "Custom Survey" : "Simple Survey";

const getSurveyGuideBannerMeta = (campaign) => {
  const hasDocument = Boolean(campaign?.surveyQuestionDocument);
  return hasDocument
    ? "Open the builder to review branching flow and update the survey guide attached to this campaign."
    : "Open the builder to define questions, branching flow, and enumerator guidance for this campaign.";
};

const resolveCampaignType = (payload) => {
  const data = payload?.data || payload || {};
  const campaignType = String(data?.campaignType || data?.type || "").toLowerCase();
  const surveyType = String(data?.surveyType || "").toLowerCase();

  if (campaignType) return campaignType;
  if (surveyType === "custom") return "custom_survey";

  return "single_survey";
};

const mapPinIconUrl = (s, isSelected = false) => {
  // Create SVG circle marker with sentiment-based color
  const colors = {
    positive: "#10b981", // rgb(16, 185, 129)
    neutral: "#f59e0b", // rgb(245, 158, 11)
    negative: "#ef4444", // rgb(239, 68, 68)
  };

  const color =
    s === "positive" ? colors.positive : s === "negative" ? colors.negative : colors.neutral;

  const markerSize = isSelected ? 32 : 28;
  const center = markerSize / 2;
  const innerRadius = isSelected ? 10 : 11;
  const outerRadius = isSelected ? 14 : 13;
  const ringStroke = isSelected ? "#ffffff" : color;
  const ringOpacity = isSelected ? 0.95 : 0.3;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}">
      <circle cx="${center}" cy="${center}" r="${outerRadius}" fill="${color}" fill-opacity="0.22" stroke="${ringStroke}" stroke-opacity="${ringOpacity}" stroke-width="${isSelected ? 3 : 4}" />
      <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="${color}" stroke="${isSelected ? "#ffffff" : color}" stroke-width="${isSelected ? 2 : 0}" />
    </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const resolveCount = (source, preferredKeys = []) => {
  if (source == null) return 0;
  if (typeof source === "number") return source;
  if (Array.isArray(source)) return source.length;
  for (const key of preferredKeys) {
    const value = source?.[key];
    if (typeof value === "number") return value;
  }
  return 0;
};

const resolveDistributionEntries = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) {
    return source
      .map((item) => {
        const label =
          item?.label ??
          item?.name ??
          item?.key ??
          item?.group ??
          item?.sentiment ??
          item?.type ??
          item?.title;
        const count = Number(item?.count ?? item?.value ?? item?.total ?? 0);
        return { label, count };
      })
      .filter((item) => item.label != null);
  }
  return Object.entries(source).map(([label, count]) => ({
    label,
    count: Number(count ?? 0),
  }));
};

const resolveAgeDistributionEntries = (source) => {
  const entries = resolveDistributionEntries(source);
  const counts = AGE_DISTRIBUTION_BUCKETS.reduce((acc, label) => {
    acc[label] = 0;
    return acc;
  }, {});

  entries.forEach((entry) => {
    const bucket = resolveAgeDistributionBucket(entry?.label);
    const count = Number(entry?.count ?? 0);
    if (!Number.isFinite(count)) return;
    counts[bucket] += count;
  });

  return AGE_DISTRIBUTION_BUCKETS.map((label) => ({ label, count: counts[label] || 0 }));
};

const formatEnumeratorInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 3);

  if (parts.length === 0) return "EN";

  const lowerJoiners = new Set(["de", "del", "dela", "da", "dos", "du", "la", "le"]);

  return parts
    .map((part, idx) => {
      const ch = part[0] || "";
      if (idx > 0 && lowerJoiners.has(part.toLowerCase())) return ch.toLowerCase();
      return ch.toUpperCase();
    })
    .join("");
};

const resolveEnumeratorPerformanceEntries = (source) => {
  if (!source) return [];

  const rows = Array.isArray(source)
    ? source
    : source?.enumerators || source?.items || source?.data || [];

  return rows
    .map((item, idx) => {
      const name =
        item?.name ||
        item?.enumerator ||
        item?.enumeratorName ||
        item?.userName ||
        `Enumerator ${idx + 1}`;
      const initials = item?.initials || formatEnumeratorInitials(name) || "EN";
      const count = Number(item?.count ?? item?.total ?? item?.processed ?? item?.responses ?? 0);

      return { name, initials, count };
    })
    .filter((row) => row.count >= 0)
    .sort((a, b) => b.count - a.count);
};

const toFilterValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
};

const toParameterFilterValue = (value) => toFilterValue(value) || "Unknown";

const RECORDING_FILTER_DEFS = [
  {
    key: "sent",
    title: "Sentiment",
    sortOrder: ["negative", "neutral", "positive"],
    getValue: (rec) => toFilterValue(String(rec?.sentiment || "").toLowerCase()),
    formatLabel: (value) => value.charAt(0).toUpperCase() + value.slice(1),
  },
  {
    key: "enumerator",
    title: "Enumerator",
    getValue: (rec) => toParameterFilterValue(rec?.name || rec?.enumerator),
  },
  { key: "gender", title: "Gender", getValue: (rec) => toParameterFilterValue(rec?.sex) },
  { key: "age", title: "Age Group", getValue: (rec) => toParameterFilterValue(rec?.ageGroup) },
  {
    key: "cls",
    title: "Social Class",
    getValue: (rec) => toParameterFilterValue(rec?.socialClass),
  },
  { key: "area", title: "Area Type", getValue: (rec) => toParameterFilterValue(rec?.areaType) },
  {
    key: "civil",
    title: "Civil Status",
    getValue: (rec) => toParameterFilterValue(rec?.civilStatus),
  },
  {
    key: "employ",
    title: "Employment",
    getValue: (rec) => toParameterFilterValue(rec?.occupation),
  },
  {
    key: "media",
    title: "Primary Media",
    getValue: (rec) =>
      toParameterFilterValue(
        rec?.primaryMedia || rec?.mediaSource || rec?.media || rec?.sourceOfInfo,
      ),
  },
  {
    key: "voter",
    title: "Voter Status",
    getValue: (rec) => toParameterFilterValue(rec?.voterStatus),
  },
  { key: "vote", title: "Vote Intent", getValue: (rec) => toParameterFilterValue(rec?.voteIntent) },
];

const createRecordingFilterState = () =>
  RECORDING_FILTER_DEFS.reduce((acc, def) => {
    acc[def.key] = [];
    return acc;
  }, {});

const cloneRecordingFilterState = (state) =>
  RECORDING_FILTER_DEFS.reduce((acc, def) => {
    acc[def.key] = Array.isArray(state?.[def.key]) ? [...state[def.key]] : [];
    return acc;
  }, {});

const getMaxEntryCount = (entries) => Math.max(...entries.map((e) => Number(e?.count || 0)), 1);

const parseEnumeratorCount = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatRecordIdValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";

  const digitsOnly = (raw.match(/\d+/g) || []).join("");
  if (!digitsOnly) return raw;
  return digitsOnly.length < 6 ? digitsOnly.padStart(6, "0") : digitsOnly;
};

const formatRecordIdLabel = (value) => {
  return `ID# ${formatRecordIdValue(value)}`;
};

const normalizeSentimentLabel = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw.includes("positive") || raw === "pos") return "positive";
  if (raw.includes("negative") || raw === "neg") return "negative";
  if (raw.includes("neutral") || raw.includes("mixed")) return "neutral";
  return null;
};

const resolveSentimentBreakdown = (sentimentSource, processedSource) => {
  const counts = {
    positive: 0,
    negative: 0,
    neutral: 0,
  };

  const sentimentCandidates = [
    sentimentSource,
    sentimentSource?.data,
    sentimentSource?.distribution,
    sentimentSource?.sentiment,
    sentimentSource?.counts,
    sentimentSource?.result,
  ].filter(Boolean);

  let hasDistributionData = false;

  sentimentCandidates.forEach((candidate) => {
    const entries = resolveDistributionEntries(candidate);
    entries.forEach((entry) => {
      const key = normalizeSentimentLabel(entry?.label);
      const value = Number(entry?.count || 0);
      if (!key || !Number.isFinite(value)) return;
      counts[key] += value;
      hasDistributionData = true;
    });
  });

  if (!hasDistributionData) {
    const processedRows = Array.isArray(processedSource?.fieldResponseProcessed)
      ? processedSource.fieldResponseProcessed
      : Array.isArray(processedSource?.data)
        ? processedSource.data
        : Array.isArray(processedSource)
          ? processedSource
          : [];

    processedRows.forEach((item) => {
      const key = normalizeSentimentLabel(item?.sentiment || item?.tone || "neutral") || "neutral";
      counts[key] += 1;
    });
  }

  return counts;
};

const getWelcomeChatMessage = (campaignCode) => ({
  id: `ai-welcome-${campaignCode}`,
  role: "assistant",
  text: `Hi! I am your campaign assistant for ${campaignCode}. Ask me for quick insights, summaries, or suggested actions.`,
});

const mapApiChatMessage = (message) => ({
  id: `chat-msg-${message?.insightChatMessageId ?? Date.now()}`,
  role: message?.role === "user" ? "user" : "assistant",
  text: message?.content || "",
});

const sortThreadsByRecent = (threads = []) => {
  if (!Array.isArray(threads)) return [];

  return [...threads].sort((a, b) => {
    const aTime = new Date(a?.lastMessageAt || a?.updatedAt || a?.createdAt || 0).getTime();
    const bTime = new Date(b?.lastMessageAt || b?.updatedAt || b?.createdAt || 0).getTime();
    return bTime - aTime;
  });
};

const formatThreadDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const getThreadLabel = (thread = {}, index = 0) => {
  const fromApi =
    thread?.title ||
    thread?.name ||
    thread?.subject ||
    thread?.lastMessagePreview ||
    thread?.lastPrompt ||
    thread?.prompt;

  if (fromApi && String(fromApi).trim()) {
    return String(fromApi).trim();
  }

  return `Conversation ${index + 1}`;
};

const mapChatThread = (thread = {}, index = 0) => ({
  ...thread,
  insightChatThreadId: thread?.insightChatThreadId,
  displayLabel: getThreadLabel(thread, index),
  displayTime: formatThreadDate(thread?.lastMessageAt || thread?.updatedAt || thread?.createdAt),
});

const formatInlineCode = (text = "") => {
  const parts = String(text).split(/(`[^`]+`)/g);
  return parts
    .map((part) => {
      if (/^`[^`]+`$/.test(part)) {
        const codeContent = part.slice(1, -1);
        return `<code>${sanitizeHtml(codeContent).replace(/<br>/g, "")}</code>`;
      }
      return sanitizeHtml(part);
    })
    .join("");
};

const formatChatMessageHtml = (text = "") => {
  const source = String(text || "").replace(/\r\n/g, "\n");
  const lines = source.split("\n");
  const blocks = [];

  let paragraphBuffer = [];
  let listBuffer = [];
  let codeBuffer = [];
  let inCodeBlock = false;

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const paragraph = paragraphBuffer.join(" ").trim();
    if (paragraph) {
      blocks.push(`<p>${formatInlineCode(paragraph)}</p>`);
    }
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!listBuffer.length) return;
    const items = listBuffer.map((item) => `<li>${formatInlineCode(item)}</li>`).join("");
    blocks.push(`<ul>${items}</ul>`);
    listBuffer = [];
  };

  const flushCode = () => {
    if (!codeBuffer.length) return;
    const codeText = codeBuffer.join("\n");
    blocks.push(`<pre><code>${sanitizeHtml(codeText).replace(/<br>/g, "\n")}</code></pre>`);
    codeBuffer = [];
  };

  lines.forEach((line) => {
    if (line.trim().startsWith("```")) {
      flushParagraph();
      flushList();

      if (inCodeBlock) {
        flushCode();
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }

    const listMatch = line.match(/^\s*(?:[-*]|\d+\.)\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      listBuffer.push(listMatch[1]);
      return;
    }

    flushList();
    paragraphBuffer.push(line.trim());
  });

  flushParagraph();
  flushList();
  flushCode();

  return blocks.join("");
};

const resolveLatestThread = (threads = []) => {
  if (!Array.isArray(threads) || threads.length === 0) return null;

  return sortThreadsByRecent(threads)[0];
};

const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const hasMapMention = (text = "") => {
  const normalized = String(text || "");
  return /\bmap\b|\bmap\s*view\b|\bmapped\b|\bgeospatial\b|\bgeo\s*view\b|\bpin(s)?\b/i.test(
    normalized,
  );
};

const extractInsightFiltersFromText = (text = "", recordings = []) => {
  const emptyState = createRecordingFilterState();
  const normalized = String(text || "").toLowerCase();

  if (!normalized.trim() || !Array.isArray(recordings) || recordings.length === 0) {
    return emptyState;
  }

  const nextState = createRecordingFilterState();

  RECORDING_FILTER_DEFS.forEach((def) => {
    const values = Array.from(
      new Set(
        recordings
          .map((recording) => def.getValue(recording))
          .filter((value) => value != null)
          .map((value) => String(value).toLowerCase()),
      ),
    );

    if (!values.length) return;

    const matchedValues = values.filter((value) => {
      const spaced = value.replace(/[_-]+/g, " ").trim();
      const candidates = new Set([value, spaced]);

      if (def.key === "sent") {
        if (value === "negative") {
          candidates.add("negative sentiment");
          candidates.add("negative sentiments");
        }
        if (value === "positive") {
          candidates.add("positive sentiment");
          candidates.add("positive sentiments");
        }
        if (value === "neutral") {
          candidates.add("neutral sentiment");
          candidates.add("mixed");
          candidates.add("mixed or neutral");
        }
      }

      return Array.from(candidates).some((candidate) => {
        if (!candidate) return false;
        const expr = new RegExp(`(^|\\W)${escapeRegExp(candidate)}(\\W|$)`, "i");
        return expr.test(normalized);
      });
    });

    if (matchedValues.length > 0) {
      nextState[def.key] = matchedValues;
    }
  });

  return nextState;
};

const TABS = [
  {
    id: "info",
    label: "Campaign Info",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
  {
    id: "exec",
    label: "Executive Summary",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    id: "map",
    label: "Map View",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
        />
      </svg>
    ),
  },
  {
    id: "recs",
    label: "Recordings",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      </svg>
    ),
  },
  {
    id: "chat",
    label: "AI Chat",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
        />
      </svg>
    ),
  },
];

const CampaignsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { campaignCode: campaignCodeParam } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [createCampaignError, setCreateCampaignError] = useState(null);
  const [isEndCampaignModalOpen, setIsEndCampaignModalOpen] = useState(false);
  const [isEndingCampaign, setIsEndingCampaign] = useState(false);
  const [endCampaignError, setEndCampaignError] = useState(null);
  const [campaignToEnd, setCampaignToEnd] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    code: "",
    name: "",
    date: "",
    location: "",
    surveyType: "custom",
    surveyDocument: null,
  });
  const [openedCampaign, setOpenedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [processedResponses, setProcessedResponses] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatTyping, setChatTyping] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [chatThreadId, setChatThreadId] = useState(null);
  const [chatInitializing, setChatInitializing] = useState(false);
  const [recordingsPage, setRecordingsPage] = useState(1);
  const [isRecordingsPageLoading, setIsRecordingsPageLoading] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [campaignRecordingTotals, setCampaignRecordingTotals] = useState({});
  const [campaignListDemographics, setCampaignListDemographics] = useState({});
  const [campaignListEnumeratorCounts, setCampaignListEnumeratorCounts] = useState({});
  const chatInitSequenceRef = useRef(0);
  const audioRef = useRef(null);
  const listAudioRef = useRef(null);
  const mapRef = useRef(null);
  const [selectedMapPin, setSelectedMapPin] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioCurrent, setAudioCurrent] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [inlineAudioRecordId, setInlineAudioRecordId] = useState(null);
  const [inlineAudioPlaying, setInlineAudioPlaying] = useState(false);
  const [inlineAudioCurrent, setInlineAudioCurrent] = useState(0);
  const [inlineAudioDuration, setInlineAudioDuration] = useState(0);
  const [isRecsFilterOpen, setIsRecsFilterOpen] = useState(false);
  const [recsFiltersApplied, setRecsFiltersApplied] = useState(() => createRecordingFilterState());
  const [recsFiltersDraft, setRecsFiltersDraft] = useState(() => createRecordingFilterState());
  const [recordingsEnumeratorOptions, setRecordingsEnumeratorOptions] = useState([]);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [isDeleteRecordingConfirmOpen, setIsDeleteRecordingConfirmOpen] = useState(false);
  const [isDeleteRecordingSuccessOpen, setIsDeleteRecordingSuccessOpen] = useState(false);
  const [isDeletingRecording, setIsDeletingRecording] = useState(false);
  const [deleteRecordingError, setDeleteRecordingError] = useState(null);
  const [campaignQuestionsData, setCampaignQuestionsData] = useState(null);
  const [, setCampaignQuestionsLoading] = useState(false);
  const [campaignType, setCampaignType] = useState(null);
  const [campaignTypeLoading, setCampaignTypeLoading] = useState(false);

  useEffect(() => {
    if (!openedCampaign?.code) return undefined;

    let isMounted = true;
    setIsRecordingsPageLoading(true);

    getFieldResponsesProcessed({
      campaignCode: openedCampaign.code,
      page: 1,
      perPage: RECORDINGS_API_PER_PAGE,
    })
      .then((processed) => {
        if (!isMounted) return;

        setProcessedResponses(processed);
      })
      .catch(() => {
        if (!isMounted) return;
        setAnalyticsError("Failed to load analytics data.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsRecordingsPageLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [openedCampaign?.code]);

  useEffect(() => {
    if (!openedCampaign?.code || activeTab !== "recs") return undefined;

    let isMounted = true;

    getFieldResponsesProcessed({
      campaignCode: openedCampaign.code,
      page: 1,
      perPage: 1000,
    })
      .then((processed) => {
        if (!isMounted) return;

        const rows = Array.isArray(processed?.fieldResponseProcessed)
          ? processed.fieldResponseProcessed
          : [];
        const counts = new Map();

        rows.forEach((item) => {
          const value = toParameterFilterValue(
            item?.name ||
              item?.enumerator ||
              item?.enumeratorName ||
              item?.userName ||
              item?.userId ||
              item?.collectedBy,
          );
          counts.set(value, (counts.get(value) || 0) + 1);
        });

        const options = Array.from(counts.entries())
          .map(([value, count]) => ({ value, label: value, count }))
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return String(a.label).localeCompare(String(b.label));
          });

        setRecordingsEnumeratorOptions(options);
      })
      .catch(() => {
        if (!isMounted) return;
        setRecordingsEnumeratorOptions([]);
      });

    return () => {
      isMounted = false;
    };
  }, [openedCampaign?.code, activeTab]);

  useEffect(() => {
    let isMounted = true;

    setCampaignsLoading(true);
    setCampaignsError(null);

    getCampaignsRequest()
      .then((data) => {
        if (!isMounted) return;
        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
            ? data
            : [];
        setCampaigns(rows.map((campaign) => normalizeCampaign(campaign)));
      })
      .catch(() => {
        if (!isMounted) return;
        setCampaigns([]);
        setCampaignsError("Failed to load campaigns.");
      })
      .finally(() => {
        if (!isMounted) return;
        setCampaignsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!campaigns.length) {
      setCampaignRecordingTotals({});
      setCampaignListDemographics({});
      setCampaignListEnumeratorCounts({});
      return undefined;
    }

    Promise.all(
      campaigns.map(async (campaign) => {
        try {
          const [total, sex, age, enumeratorPerf, processed] = await Promise.all([
            getTotalProcessedResponses({ campaignCode: campaign.code }),
            getSexDistribution({ campaignCode: campaign.code }).catch(() => null),
            getAgeDistribution({ campaignCode: campaign.code }).catch(() => null),
            getEnumeratorPerformance({ campaignCode: campaign.code }).catch(() => null),
            getFieldResponsesProcessed({ campaignCode: campaign.code, page: 1, perPage: 1 }).catch(
              () => null,
            ),
          ]);
          const enumeratorEntries = resolveEnumeratorPerformanceEntries(enumeratorPerf);
          const processedTotal =
            resolveCount(processed, ["total", "totalCount", "count", "total_count"]) ||
            resolveCount(total, ["total", "count", "processed"]);
          return [
            campaign.code,
            {
              total: processedTotal,
              sex,
              age,
              enumeratorCount: enumeratorEntries.length,
            },
          ];
        } catch {
          return [campaign.code, { total: 0, sex: null, age: null, enumeratorCount: 0 }];
        }
      }),
    ).then((entries) => {
      if (!isMounted) return;
      const totals = {};
      const demog = {};
      const enumCounts = {};
      entries.forEach(([code, data]) => {
        totals[code] = data.total;
        demog[code] = { sex: data.sex, age: data.age };
        enumCounts[code] = data.enumeratorCount;
      });
      setCampaignRecordingTotals(totals);
      setCampaignListDemographics(demog);
      setCampaignListEnumeratorCounts(enumCounts);
    });

    return () => {
      isMounted = false;
    };
  }, [campaigns]);

  useEffect(() => {
    if (!selectedRecording) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setAudioPlaying(false);
      setAudioCurrent(0);
      setAudioDuration(0);
      return;
    }

    if (listAudioRef.current) {
      listAudioRef.current.pause();
    }
    setInlineAudioPlaying(false);
  }, [selectedRecording]);

  const formatAudioTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = String(Math.floor(secs % 60)).padStart(2, "0");
    return `${m}:${s}`;
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (audioPlaying) {
      el.pause();
    } else {
      el.play();
    }
  };

  const seekAudio = (e) => {
    const el = audioRef.current;
    if (!el || !audioDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * audioDuration;
  };

  const toggleInlinePlay = (rec) => {
    const el = listAudioRef.current;
    if (!el || !rec?.audioUrl) return;

    if (inlineAudioRecordId !== rec.id) {
      setInlineAudioRecordId(rec.id);
      setInlineAudioCurrent(0);
      setInlineAudioDuration(0);
      el.src = rec.audioUrl;
      el.load();
      el.play();
      return;
    }

    if (inlineAudioPlaying) {
      el.pause();
    } else {
      el.play();
    }
  };

  const stopInlinePlay = () => {
    const el = listAudioRef.current;
    if (!el || inlineAudioRecordId == null) return;
    el.pause();
    el.currentTime = 0;
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
  };

  const seekInlineAudio = (event, recId) => {
    if (inlineAudioRecordId !== recId || !inlineAudioDuration) return;
    const el = listAudioRef.current;
    if (!el) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    el.currentTime = ratio * inlineAudioDuration;
  };

  useEffect(() => {
    if (!selectedRecording) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") setSelectedRecording(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [selectedRecording]);

  useEffect(() => {
    if (
      !isCreateModalOpen &&
      !isEndCampaignModalOpen &&
      !isDeleteRecordingConfirmOpen &&
      !isDeleteRecordingSuccessOpen
    ) {
      return undefined;
    }

    const onEscClose = (e) => {
      if (e.key === "Escape") {
        setIsCreateModalOpen(false);
        setIsEndCampaignModalOpen(false);
        setIsDeleteRecordingConfirmOpen(false);
        setIsDeleteRecordingSuccessOpen(false);
      }
    };
    window.addEventListener("keydown", onEscClose);
    return () => window.removeEventListener("keydown", onEscClose);
  }, [
    isCreateModalOpen,
    isEndCampaignModalOpen,
    isDeleteRecordingConfirmOpen,
    isDeleteRecordingSuccessOpen,
  ]);

  useEffect(() => {
    if (!mapRef.current || !openedCampaign || activeTab !== "map") return;

    // Calculate mapRecordings from processedResponses
    const apiRecords = (processedResponses?.fieldResponseProcessed || []).map((item) => ({
      id: item.id,
      name: item.name || item.respondentName || "Unknown Respondent",
      latitude: item.latitude || item.lat,
      longitude: item.longitude || item.lng,
      sentiment: item.sentiment?.toLowerCase() || "neutral",
      tone: item.tone || item.sentimentTone || "",
      location: item.location || item.address || "Unknown",
      date: item.date || item.dateCreated || "",
      enumerator: item.enumerator || item.collectedBy || "",
      concerns: item.concerns || item.keyConcerns || [],
      summary: item.summary || item.remarks || item.notes || "",
      demographics: {
        ageRange: item.ageRange || item.age || "",
        gender: item.gender || item.sex || "",
        occupation: item.occupation || "",
        yearsInArea: item.yearsInArea || item.yearsInAreaCategory || "",
        socialClass: item.socialClass || item.class || "",
      },
      political: {
        voterType: item.voterType || item.voterStatus || "",
        intent: item.intent || "",
        natGov: item.nationalGovernance || item.natGov || "",
        mayor: item.mayor || item.mayorAssessment || "",
      },
      traits: {
        desirable: Array.isArray(item.desiredTraits)
          ? item.desiredTraits
          : (item.desiredTraits || "").split(/,|;/).filter(Boolean),
        rejected: Array.isArray(item.rejectedTraits)
          ? item.rejectedTraits
          : (item.rejectedTraits || "").split(/,|;/).filter(Boolean),
      },
    }));

    const displayRecords =
      apiRecords.length > 0 ? apiRecords : openedCampaign.details.recordings || [];
    const filteredRecords = displayRecords.filter((rec) =>
      RECORDING_FILTER_DEFS.every((def) => {
        const selected = recsFiltersApplied[def.key] || [];
        if (selected.length === 0) return true;
        const value = def.getValue(rec);
        return value != null && selected.includes(value);
      }),
    );

    const mapRecordings = filteredRecords.filter(
      (rec) => Number.isFinite(rec.latitude) && Number.isFinite(rec.longitude),
    );

    if (!mapRecordings.length) return;

    if (mapRecordings.length === 1) {
      mapRef.current.setCenter({
        lat: mapRecordings[0].latitude,
        lng: mapRecordings[0].longitude,
      });
      mapRef.current.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    mapRecordings.forEach((rec) => {
      bounds.extend({ lat: rec.latitude, lng: rec.longitude });
    });
    mapRef.current.fitBounds(bounds, 48);
  }, [openedCampaign, processedResponses, activeTab, recsFiltersApplied]);

  const onOpenCreateModal = () => {
    setCreateCampaignError(null);
    setIsCreateModalOpen(true);
  };
  const onCloseCreateModal = () => {
    setCreateCampaignError(null);
    setIsCreateModalOpen(false);
  };
  const onCampaignFormChange = (event) => {
    const { name, value } = event.target;
    setCampaignForm((p) => ({ ...p, [name]: name === "code" ? value.toUpperCase() : value }));
  };
  const onCampaignFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    setCampaignForm((prev) => ({ ...prev, surveyDocument: file }));
  };
  const onCreateCampaign = async (e) => {
    e.preventDefault();
    setCreateCampaignError(null);

    const formElement = e.currentTarget;

    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setIsCreatingCampaign(true);

    try {
      const campaignDateISO = toCampaignDateISO(campaignForm.date);
      const endDate = new Date(campaignDateISO);
      endDate.setUTCDate(endDate.getUTCDate() + 30);

      const created = await createCampaignRequest({
        description: campaignForm.name,
        campaignCode: campaignForm.code,
        campaignDate: campaignDateISO,
        status: "active",
        endDate: endDate.toISOString(),
        location: campaignForm.location,
        surveyType: campaignForm.surveyType,
        campaignType: campaignForm.surveyType === "custom" ? "custom_survey" : "document",
        surveyQuestionDocument: campaignForm.surveyDocument,
      });

      const normalized = normalizeCampaign(created);
      setCampaigns((prev) => {
        const filtered = prev.filter((campaign) => campaign.code !== normalized.code);
        return [normalized, ...filtered];
      });

      setCampaignForm({
        code: "",
        name: "",
        date: "",
        location: "",
        surveyType: "custom",
        surveyDocument: null,
      });
      setIsCreateModalOpen(false);

      if (normalized.surveyType === "custom") {
        history.push(Path.getCampaignSurveyBuilder(normalized.code), { campaign: normalized });
      }
    } catch {
      setCreateCampaignError("Failed to create campaign.");
    } finally {
      setIsCreatingCampaign(false);
    }
  };
  const onOpenEndCampaignModal = (c) => {
    setEndCampaignError(null);
    setCampaignToEnd(c);
    setIsEndCampaignModalOpen(true);
  };
  const onCloseEndCampaignModal = () => {
    setEndCampaignError(null);
    setIsEndCampaignModalOpen(false);
    setCampaignToEnd(null);
  };
  const onConfirmEndCampaign = async () => {
    if (!campaignToEnd?.code) return;

    setEndCampaignError(null);
    setIsEndingCampaign(true);

    try {
      const ended = await endCampaignRequest({ campaignCode: campaignToEnd.code });
      const normalized = normalizeCampaign(ended || campaignToEnd);

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.code === campaignToEnd.code
            ? {
                ...campaign,
                ...normalized,
                status: normalized.status || "ended",
              }
            : campaign,
        ),
      );

      if (openedCampaign?.code === campaignToEnd.code) {
        setOpenedCampaign((prev) =>
          prev
            ? {
                ...prev,
                status: normalized.status || "ended",
              }
            : prev,
        );
      }

      setIsEndCampaignModalOpen(false);
      setCampaignToEnd(null);
    } catch {
      setEndCampaignError("Failed to end campaign.");
    } finally {
      setIsEndingCampaign(false);
    }
  };
  const getPercent = (count, total) => (!total ? 0 : Math.round((count / total) * 100));

  const openRecsFilterPanel = () => {
    setRecsFiltersDraft(cloneRecordingFilterState(recsFiltersApplied));
    setIsRecsFilterOpen(true);
  };

  const closeRecsFilterPanel = () => setIsRecsFilterOpen(false);

  const toggleRecsDraftFilter = (key, value) => {
    setRecsFiltersDraft((prev) => {
      const next = cloneRecordingFilterState(prev);

      if (value === "__all__") {
        next[key] = [];
        return next;
      }

      const selected = new Set(next[key]);
      if (selected.has(value)) {
        selected.delete(value);
      } else {
        selected.add(value);
      }
      next[key] = Array.from(selected);
      return next;
    });
  };

  const resetRecsDraftFilters = () => {
    setRecsFiltersDraft(createRecordingFilterState());
  };

  const applyRecsFilters = () => {
    setRecsFiltersApplied(cloneRecordingFilterState(recsFiltersDraft));
    setRecordingsPage(1);
    setIsRecsFilterOpen(false);
  };

  const clearAllRecsFilters = () => {
    const empty = createRecordingFilterState();
    setRecsFiltersApplied(empty);
    setRecsFiltersDraft(empty);
    setRecordingsPage(1);
  };

  const onOpenDeleteRecordingModal = (recording) => {
    setRecordingToDelete(recording || null);
    setDeleteRecordingError(null);
    setIsDeleteRecordingConfirmOpen(true);
  };

  const onCloseDeleteRecordingModal = () => {
    if (isDeletingRecording) return;
    setIsDeleteRecordingConfirmOpen(false);
    setRecordingToDelete(null);
    setDeleteRecordingError(null);
  };

  const onCloseDeleteRecordingSuccessModal = () => {
    setIsDeleteRecordingSuccessOpen(false);
    setRecordingToDelete(null);
    setDeleteRecordingError(null);
  };

  const onConfirmDeleteRecording = async () => {
    if (!recordingToDelete || isDeletingRecording) return;

    const resolvedFieldResponseProcessedId = Number(
      recordingToDelete.fieldResponseProcessedId ?? recordingToDelete.id,
    );

    if (!Number.isFinite(resolvedFieldResponseProcessedId)) {
      setDeleteRecordingError("Recording ID is invalid. Please refresh and try again.");
      return;
    }

    setIsDeletingRecording(true);
    setDeleteRecordingError(null);

    try {
      await deleteFieldResponseProcessed({
        fieldResponseProcessedId: resolvedFieldResponseProcessedId,
      });
    } catch (error) {
      if (error?.status === 404 || error?.data?.errorCode === "FR1016") {
        setDeleteRecordingError("Recording not found. It may have already been deleted.");
      } else if (error?.status === 401 || error?.error === "unauthorized") {
        setDeleteRecordingError("Unauthorized request. Please sign in again.");
      } else {
        setDeleteRecordingError(error?.data?.message || "Failed to delete recording.");
      }
      setIsDeletingRecording(false);
      return;
    }

    const deletingId = String(recordingToDelete.id ?? "");
    const deletingFieldResponseId = String(recordingToDelete.fieldResponseId ?? "");

    const shouldKeepItem = (item) => {
      const candidates = [
        item?.fieldResponseProcessedId,
        item?.fieldResponseId,
        item?.field_response_id,
        item?.id,
      ]
        .map((entry) => String(entry ?? ""))
        .filter(Boolean);

      if (deletingId && candidates.includes(deletingId)) return false;
      if (deletingFieldResponseId && candidates.includes(deletingFieldResponseId)) return false;
      return true;
    };

    setProcessedResponses((prev) => {
      if (!prev || !Array.isArray(prev?.fieldResponseProcessed)) return prev;

      const previousRows = prev.fieldResponseProcessed;
      const nextRows = previousRows.filter((item) => shouldKeepItem(item));
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount <= 0) return prev;

      const next = { ...prev, fieldResponseProcessed: nextRows };
      const totalKeys = ["total", "totalCount", "count", "total_count"];
      totalKeys.forEach((key) => {
        if (typeof prev?.[key] === "number") {
          next[key] = Math.max(0, prev[key] - removedCount);
        }
      });

      return next;
    });

    setOpenedCampaign((prev) => {
      if (!prev || !Array.isArray(prev?.details?.recordings)) return prev;

      const previousRows = prev.details.recordings;
      const nextRows = previousRows.filter((item) => shouldKeepItem(item));
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount <= 0) return prev;

      const previousTotal = Number(prev?.details?.totalRecordings ?? 0);

      return {
        ...prev,
        details: {
          ...prev.details,
          recordings: nextRows,
          totalRecordings: Number.isFinite(previousTotal)
            ? Math.max(0, previousTotal - removedCount)
            : prev?.details?.totalRecordings,
        },
      };
    });

    if (
      selectedRecording &&
      (String(selectedRecording.id ?? "") === deletingId ||
        String(selectedRecording.fieldResponseId ?? "") === deletingFieldResponseId)
    ) {
      setSelectedRecording(null);
    }

    setIsDeletingRecording(false);
    setDeleteRecordingError(null);
    setIsDeleteRecordingConfirmOpen(false);
    setIsDeleteRecordingSuccessOpen(true);
  };

  const refreshChatThreadsForCampaign = async ({ campaignCode, preferredThreadId = null }) => {
    if (!campaignCode) return [];

    const threadsResult = await getChatThreads({ campaignCode, page: 1, perPage: 20 });
    const threads = sortThreadsByRecent(threadsResult?.threads || []).map((thread, index) =>
      mapChatThread(thread, index),
    );

    setChatThreads(threads);

    if (
      preferredThreadId &&
      threads.some((thread) => thread?.insightChatThreadId === preferredThreadId)
    ) {
      setChatThreadId(preferredThreadId);
    }

    return threads;
  };

  const initializeChatForCampaign = async (campaignCode) => {
    const sequence = ++chatInitSequenceRef.current;

    setChatInitializing(true);
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatMessages([]);

    try {
      let threads = await refreshChatThreadsForCampaign({ campaignCode });
      if (sequence !== chatInitSequenceRef.current) return;

      const latestThread = resolveLatestThread(threads);
      const createdThread = latestThread ? null : await createChatThread({ campaignCode });
      if (sequence !== chatInitSequenceRef.current) return;

      if (createdThread?.insightChatThreadId) {
        threads = await refreshChatThreadsForCampaign({
          campaignCode,
          preferredThreadId: createdThread.insightChatThreadId,
        });
        if (sequence !== chatInitSequenceRef.current) return;
      }

      const resolvedThread =
        (createdThread?.insightChatThreadId
          ? threads.find(
              (thread) => thread?.insightChatThreadId === createdThread.insightChatThreadId,
            )
          : null) || resolveLatestThread(threads);
      const resolvedThreadId = resolvedThread?.insightChatThreadId;

      if (!resolvedThreadId) {
        setChatMessages([getWelcomeChatMessage(campaignCode)]);
        return;
      }

      setChatThreadId(resolvedThreadId);

      const messagesResult = await getChatThreadMessages({
        insightChatThreadId: resolvedThreadId,
        page: 1,
        perPage: 100,
      });
      if (sequence !== chatInitSequenceRef.current) return;

      const mappedMessages = Array.isArray(messagesResult?.messages)
        ? messagesResult.messages.map(mapApiChatMessage)
        : [];

      setChatMessages(
        mappedMessages.length > 0 ? mappedMessages : [getWelcomeChatMessage(campaignCode)],
      );
    } catch {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatThreads([]);
      setChatThreadId(null);
      setChatMessages([getWelcomeChatMessage(campaignCode)]);
    } finally {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatInitializing(false);
    }
  };

  const loadChatThreadMessagesForCampaign = async ({
    campaignCode,
    insightChatThreadId,
    keepInput = false,
  }) => {
    if (!campaignCode || !insightChatThreadId) return;

    const sequence = ++chatInitSequenceRef.current;

    setChatInitializing(true);
    setChatTyping(false);
    if (!keepInput) {
      setChatInput("");
    }
    setChatThreadId(insightChatThreadId);

    try {
      const messagesResult = await getChatThreadMessages({
        insightChatThreadId,
        page: 1,
        perPage: 100,
      });
      if (sequence !== chatInitSequenceRef.current) return;

      const mappedMessages = Array.isArray(messagesResult?.messages)
        ? messagesResult.messages.map(mapApiChatMessage)
        : [];

      setChatMessages(
        mappedMessages.length > 0 ? mappedMessages : [getWelcomeChatMessage(campaignCode)],
      );
    } catch {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatMessages([getWelcomeChatMessage(campaignCode)]);
    } finally {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatInitializing(false);
    }
  };

  const onSelectChatThread = async (insightChatThreadId) => {
    if (!openedCampaign || !insightChatThreadId || chatInitializing || chatTyping) return;
    if (insightChatThreadId === chatThreadId) return;

    await loadChatThreadMessagesForCampaign({
      campaignCode: openedCampaign.code,
      insightChatThreadId,
    });
  };

  const onCreateNewChatThread = async () => {
    if (!openedCampaign || chatInitializing || chatTyping) return;

    const campaignCode = openedCampaign.code;

    setChatInitializing(true);
    setChatTyping(false);
    setChatInput("");

    try {
      const createdThread = await createChatThread({ campaignCode });
      const createdThreadId = createdThread?.insightChatThreadId;

      if (!createdThreadId) {
        setChatMessages([getWelcomeChatMessage(campaignCode)]);
        return;
      }

      await refreshChatThreadsForCampaign({
        campaignCode,
        preferredThreadId: createdThreadId,
      });

      await loadChatThreadMessagesForCampaign({
        campaignCode,
        insightChatThreadId: createdThreadId,
        keepInput: true,
      });
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-error-${Date.now()}`,
          role: "assistant",
          text: "I couldn't start a new thread right now. Please try again.",
        },
      ]);
      setChatInitializing(false);
    }
  };

  const openCampaign = (campaign, options = {}) => {
    const { skipNavigation = false } = options;
    const code = String(campaign?.code || "").trim();

    if (!skipNavigation && code) {
      const targetPath = `/${encodeURIComponent(code)}`;
      if (location.pathname !== targetPath) {
        history.push(targetPath);
      }
    }

    setOpenedCampaign(campaign);
    setActiveTab("info");
    setRecordingsPage(1);
    setChatInput("");
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatInitializing(false);
    initializeChatForCampaign(campaign.code);
    setAnalytics(null);
    setProcessedResponses(null);
    setRecordingsEnumeratorOptions([]);
    setAnalyticsError(null);
    setIsRecsFilterOpen(false);
    setRecsFiltersApplied(createRecordingFilterState());
    setRecsFiltersDraft(createRecordingFilterState());
    if (listAudioRef.current) {
      listAudioRef.current.pause();
      listAudioRef.current.currentTime = 0;
    }
    setInlineAudioRecordId(null);
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
    setInlineAudioDuration(0);
    setCampaignQuestionsData(null);
    setCampaignQuestionsLoading(false);
    setCampaignType(null);
    setCampaignTypeLoading(false);
    setAnalyticsLoading(true);

    if (campaign.code) {
      setCampaignTypeLoading(true);
      getCampaignByCodeRequest({ campaignCode: campaign.code })
        .then((data) => {
          const resolvedCampaignType = resolveCampaignType(data);
          setCampaignType(resolvedCampaignType);

          if (resolvedCampaignType === "custom_survey" && campaign.campaignId) {
            setCampaignQuestionsLoading(true);
            getCampaignQuestionsRequest({ campaignId: campaign.campaignId })
              .then((questionsData) => {
                setCampaignQuestionsData(questionsData || null);
              })
              .catch(() => {
                setCampaignQuestionsData(null);
              })
              .finally(() => {
                setCampaignQuestionsLoading(false);
              });
          }
        })
        .catch(() => {
          setCampaignType(null);
        })
        .finally(() => {
          setCampaignTypeLoading(false);
        });
    }

    getTotalProcessedResponses({ campaignCode: campaign.code })
      .then((total) =>
        Promise.all([
          Promise.resolve(total),
          getCategoryDistribution({ campaignCode: campaign.code }),
          getSentimentDistribution({ campaignCode: campaign.code }),
          getEmploymentSectorDistribution({ campaignCode: campaign.code }),
          getEnumeratorPerformance({ campaignCode: campaign.code }),
          getKeyConcerns({ campaignCode: campaign.code }),
          getAgeDistribution({ campaignCode: campaign.code }),
          getSexDistribution({ campaignCode: campaign.code }),
        ]),
      )
      .then(
        ([total, category, sentiment, employment, enumeratorPerformance, concerns, age, sex]) => {
          setAnalytics({
            total,
            category,
            sentiment,
            employment,
            enumeratorPerformance,
            concerns,
            age,
            sex,
          });
        },
      )
      .catch(() => {
        setAnalyticsError("Failed to load analytics data.");
      })
      .finally(() => {
        setAnalyticsLoading(false);
      });
  };

  const clearOpenedCampaignState = () => {
    setOpenedCampaign(null);
    setRecordingsPage(1);
    setAnalytics(null);
    setProcessedResponses(null);
    setRecordingsEnumeratorOptions([]);
    setAnalyticsError(null);
    setChatInput("");
    setChatMessages([]);
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatInitializing(false);
    chatInitSequenceRef.current += 1;
    setCampaignQuestionsData(null);
    setCampaignQuestionsLoading(false);
    setCampaignType(null);
    setCampaignTypeLoading(false);
    setIsRecsFilterOpen(false);
    setRecsFiltersApplied(createRecordingFilterState());
    setRecsFiltersDraft(createRecordingFilterState());
    if (listAudioRef.current) {
      listAudioRef.current.pause();
      listAudioRef.current.currentTime = 0;
    }
    setInlineAudioRecordId(null);
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
    setInlineAudioDuration(0);
  };

  const goBack = () => {
    if (location.pathname !== Path.Campaigns) {
      history.push(Path.Campaigns);
    }

    clearOpenedCampaignState();
  };

  const onOpenSurveyBuilder = (campaign) => {
    const code = String(campaign?.code || "").trim();
    if (!code) return;

    const isCustomSurvey = campaignType === "custom_survey";

    history.push(Path.getCampaignSurveyBuilder(code), {
      campaign: {
        ...campaign,
        campaignType: isCustomSurvey ? "custom_survey" : campaign?.campaignType,
        surveyType: isCustomSurvey ? "custom" : campaign?.surveyType,
      },
      questions: campaignQuestionsData?.questions || null,
    });
  };

  useEffect(() => {
    if (campaignCodeParam) return;
    if (!openedCampaign) return;

    // Browser back from /{campaignCode} -> /campaigns should return to list state.
    clearOpenedCampaignState();
  }, [campaignCodeParam, openedCampaign]);

  useEffect(() => {
    if (!campaignCodeParam || !campaigns.length) return;

    const decodedCode = decodeURIComponent(campaignCodeParam);
    if (openedCampaign?.code === decodedCode) return;

    const matchingCampaign = campaigns.find(
      (campaign) => String(campaign.code || "").toLowerCase() === decodedCode.toLowerCase(),
    );

    if (matchingCampaign) {
      openCampaign(matchingCampaign, { skipNavigation: true });
    }
    // openCampaign is intentionally excluded to avoid re-running campaign bootstrap repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCodeParam, campaigns, openedCampaign]);

  const totalRecordingsFromApi = campaigns.reduce(
    (sum, campaign) => sum + (campaignRecordingTotals[campaign.code] ?? 0),
    0,
  );

  const totalEnumeratorsFromList = campaigns.reduce((sum, campaign) => {
    const count =
      campaignListEnumeratorCounts[campaign.code] ?? parseEnumeratorCount(campaign.enumerators);
    return sum + count;
  }, 0);

  const onSendChat = async () => {
    const prompt = chatInput.trim();
    if (!prompt || !openedCampaign || chatTyping || chatInitializing) return;

    const campaign = openedCampaign;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: prompt,
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    setChatTyping(true);

    try {
      let resolvedThreadId = chatThreadId;

      if (!resolvedThreadId) {
        const thread = await createChatThread({ campaignCode: campaign.code });
        resolvedThreadId = thread?.insightChatThreadId;
        setChatThreadId(resolvedThreadId || null);

        if (resolvedThreadId) {
          await refreshChatThreadsForCampaign({
            campaignCode: campaign.code,
            preferredThreadId: resolvedThreadId,
          });
        }
      }

      if (!resolvedThreadId) {
        throw new Error("Missing thread id");
      }

      const response = await createChatThreadMessage({
        insightChatThreadId: resolvedThreadId,
        prompt,
      });

      const assistantMessage = response?.assistantMessage;
      const assistantText = assistantMessage?.content || "I could not generate a response.";

      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-${assistantMessage?.insightChatMessageId ?? Date.now()}`,
          role: "assistant",
          text: assistantText,
        },
      ]);

      await refreshChatThreadsForCampaign({
        campaignCode: campaign.code,
        preferredThreadId: resolvedThreadId,
      });

      const combinedInsightText = `${prompt} ${assistantText}`;
      const mapMentioned = hasMapMention(combinedInsightText);

      if (mapMentioned) {
        const insightRecords = (processedResponses?.fieldResponseProcessed || []).map((item) => ({
          sentiment: String(item?.sentiment || item?.tone || "neutral").toLowerCase(),
          sex: item?.sex || item?.gender || "",
          ageGroup:
            item?.ageGroup ||
            item?.age_group ||
            toAgeGroupFromAge(item?.age || item?.respondentAge || item?.demographics?.age),
          socialClass: item?.socialClass || item?.social_class || "",
          areaType: item?.areaType || item?.area_type || "",
          civilStatus: item?.civilStatus || item?.civil_status || "",
          occupation: item?.occupation || "",
          primaryMedia:
            item?.primaryMedia || item?.mediaSource || item?.media || item?.sourceOfInfo || "",
          voterStatus: item?.voterStatus || item?.voter_status || "",
          voteIntent: item?.voteIntent || item?.vote_intent || "",
        }));

        const insightFilters = extractInsightFiltersFromText(assistantText, insightRecords);
        const hasAnyFilter = Object.values(insightFilters).some(
          (values) => Array.isArray(values) && values.length > 0,
        );

        if (hasAnyFilter) {
          setRecsFiltersApplied(insightFilters);
          setRecsFiltersDraft(cloneRecordingFilterState(insightFilters));
          setRecordingsPage(1);
        }

        setActiveTab("map");
      }
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-error-${Date.now()}`,
          role: "assistant",
          text: "I couldn't process that request right now. Please try again.",
        },
      ]);
    } finally {
      setChatTyping(false);
    }
  };

  const endCampaignModal = isEndCampaignModalOpen && campaignToEnd && (
    <div className={styles.modalRoot} role="presentation" onMouseDown={onCloseEndCampaignModal}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-campaign-title"
        className={styles.modalContent}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 id="end-campaign-title">End Campaign</h2>
        </div>
        <div className={styles.endCampaignBody}>
          <p className={styles.endCampaignMessage}>
            Are you sure you want to end{" "}
            <span className={styles.campaignNameHighlight}>{`"${campaignToEnd.title}"`}</span>?
          </p>
          <div className={styles.warningBox}>
            <p className={styles.warningText}>
              This will mark the campaign as ended. No new recordings can be submitted under this
              campaign code.
            </p>
          </div>
          {endCampaignError && (
            <div className={styles.analyticsError} style={{ marginBottom: 10 }}>
              {endCampaignError}
            </div>
          )}
          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelButton} onClick={onCloseEndCampaignModal}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.endConfirmButton}
              onClick={onConfirmEndCampaign}
              disabled={isEndingCampaign}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.endIconSmall}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <rect x="9" y="9" width="6" height="6" rx="1" />
              </svg>
              {isEndingCampaign ? "Ending..." : "End Campaign"}
            </button>
          </div>
        </div>
        <button type="button" className={styles.closeButton} onClick={onCloseEndCampaignModal}>
          <span aria-hidden="true">x</span>
          <span className={styles.srOnly}>Close</span>
        </button>
      </div>
    </div>
  );

  const createModal = isCreateModalOpen && (
    <div className={styles.modalRoot} role="presentation" onMouseDown={onCloseCreateModal}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-campaign-title"
        className={`${styles.modalContent} ${styles.createBox}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div id="create-campaign-title" className={styles.createTitle}>
          Create New Campaign
        </div>
        <form className={styles.createBody} onSubmit={onCreateCampaign}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-code">
              Campaign Code (max 6 chars)
            </label>
            <input
              className={styles.fieldInput}
              id="nc-code"
              name="code"
              maxLength={6}
              placeholder="e.g. LAG004"
              value={campaignForm.code}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-name">
              Campaign Name
            </label>
            <input
              className={styles.fieldInput}
              id="nc-name"
              name="name"
              placeholder="e.g. North Laguna Pulse Check"
              value={campaignForm.name}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-date">
              Campaign Start Date
            </label>
            <input
              className={styles.fieldInput}
              type="date"
              id="nc-date"
              name="date"
              value={campaignForm.date}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-loc">
              Location
            </label>
            <input
              className={styles.fieldInput}
              id="nc-loc"
              name="location"
              placeholder="e.g. Calamba and Binan, Laguna"
              value={campaignForm.location}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>Survey Type</div>
            <div className={styles.surveyTypeGrid} role="radiogroup" aria-label="Survey Type">
              {SURVEY_TYPE_OPTIONS.map((option) => {
                const checked = campaignForm.surveyType === option.value;

                return (
                  <label
                    key={option.value}
                    className={`${styles.surveyTypeCard} ${checked ? styles.surveyTypeCardActive : ""}`}
                  >
                    <input
                      className={styles.surveyTypeInput}
                      type="radio"
                      name="surveyType"
                      value={option.value}
                      checked={checked}
                      onChange={onCampaignFormChange}
                    />
                    <span className={styles.surveyTypeCardTop}>
                      <span className={styles.surveyTypeIcon} aria-hidden="true">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.9"
                        >
                          <rect x="4" y="5" width="16" height="14" rx="3" />
                          <path d="M8 9h8M8 12h6M8 15h4" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className={styles.surveyTypeCheck} aria-hidden="true" />
                    </span>
                    <span className={styles.surveyTypeTitle}>{option.label}</span>
                    <span className={styles.surveyTypeDescription}>{option.description}</span>
                  </label>
                );
              })}
            </div>
            <div className={styles.surveyTypeHint}>
              {campaignForm.surveyType === "custom"
                ? "After creating, you can continue into the Survey Builder to compose questions, answer options, and branching flow for enumerators."
                : "Use Simple Survey when enumerators only need free-form recordings, with an optional survey guide document attached below."}
            </div>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-doc">
              Survey Questions Document (Optional)
            </label>
            <input
              className={`${styles.fieldInput} ${styles.fileInput}`}
              id="nc-doc"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={onCampaignFileChange}
            />
          </div>
          <div className={styles.createActions}>
            <button
              type="button"
              className={styles.createCancelButton}
              onClick={onCloseCreateModal}
              disabled={isCreatingCampaign}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.createSubmitButton}
              disabled={isCreatingCampaign}
            >
              {isCreatingCampaign
                ? "Creating..."
                : campaignForm.surveyType === "custom"
                  ? "Create & Build Survey"
                  : "Create Campaign"}
            </button>
          </div>
          {createCampaignError && (
            <div className={styles.analyticsError}>{createCampaignError}</div>
          )}
        </form>
      </div>
    </div>
  );

  /* ── CAMPAIGN DETAIL VIEW ── */
  if (openedCampaign) {
    const c = openedCampaign;
    const d = c.details;
    const statusColor = c.status === "active" ? "#10b981" : "#6b7280";
    const totalProcessed = resolveCount(analytics?.total, ["total", "count", "processed"]);
    const resolvedSentiment = resolveSentimentBreakdown(analytics?.sentiment, processedResponses);
    const ageEntries = resolveAgeDistributionEntries(analytics?.age);
    const sexEntries = resolveDistributionEntries(analytics?.sex);
    const apiRecordings = (processedResponses?.fieldResponseProcessed || []).map((item, index) => {
      const pickField = (...values) => {
        for (const value of values) {
          if (value == null) continue;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) return trimmed;
            continue;
          }
          if (Array.isArray(value)) {
            if (value.length > 0) return value;
            continue;
          }
          return value;
        }
        return "";
      };

      const parseList = (value) => {
        if (Array.isArray(value)) {
          return value.map((entry) => String(entry || "").trim()).filter(Boolean);
        }
        if (!value) return [];
        return String(value)
          .split(/,|;/)
          .map((entry) => entry.trim())
          .filter(Boolean);
      };

      const sentiment = String(item?.sentiment || item?.tone || "neutral").toLowerCase();
      const safeSentiment = ["positive", "negative", "neutral"].includes(sentiment)
        ? sentiment
        : "neutral";

      const timestampCandidate = pickField(
        item?.timestamp,
        item?.processedAt,
        item?.createdAt,
        item?.recordedAt,
        item?.submittedAt,
        item?.surveyDateTime,
        item?.responseDateTime,
        item?.dateTime,
      );
      const parsedTimestamp = timestampCandidate ? new Date(timestampCandidate) : null;
      const hasValidTimestamp =
        parsedTimestamp instanceof Date && !Number.isNaN(parsedTimestamp.getTime());

      const dateStr = hasValidTimestamp
        ? parsedTimestamp.toISOString().split("T")[0]
        : pickField(item?.date, item?.recordingDate, item?.surveyDate, item?.dateCreated) || "-";
      const timeStr = hasValidTimestamp
        ? parsedTimestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
        : pickField(item?.time, item?.recordingTime, item?.surveyTime, item?.timeCreated, "");

      const enumerator = item?.name || item?.userId || "Unknown Enumerator";
      const initials =
        enumerator
          .split(/[\s_-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join("") || "UE";
      const lat = item?.latitude ?? item?.lat ?? null;
      const lng = item?.longitude ?? item?.lng ?? null;
      const coordsStr =
        lat != null && lng != null ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}` : null;
      const concernChips = parseList(
        pickField(item?.keyConcerns, item?.key_concerns, item?.concerns, item?.issues),
      );

      const desiredTraits = parseList(
        pickField(
          item?.desiredTraits,
          item?.desired_traits,
          item?.leaderPreferences?.desiredTraits,
          item?.leaderPreferences?.desired,
        ),
      );
      const rejectedTraits = parseList(
        pickField(
          item?.rejectedTraits,
          item?.rejected_traits,
          item?.leaderPreferences?.rejectedTraits,
          item?.leaderPreferences?.rejected,
        ),
      );

      const derivedAgeGroup = toAgeGroupFromAge(
        pickField(item?.age, item?.respondentAge, item?.demographics?.age, item?.ageYears),
      );

      return {
        id: item?.fieldResponseProcessedId || item?.fieldResponseId || `rec-${index}`,
        fieldResponseProcessedId: pickField(
          item?.fieldResponseProcessedId,
          item?.field_response_processed_id,
          item?.fieldResponseId,
          item?.field_response_id,
        ),
        fieldResponseId: pickField(item?.fieldResponseId, item?.field_response_id),
        enumerator,
        initials,
        location: pickField(item?.address, item?.location, item?.place) || "Unknown Location",
        barangay: pickField(item?.barangay, item?.village, item?.brgy),
        identifiedBarangay: pickField(item?.identifiedBarangay, item?.identified_barangay),
        latitude: lat != null ? Number(lat) : null,
        longitude: lng != null ? Number(lng) : null,
        coordsStr,
        sentiment: safeSentiment,
        category: pickField(item?.category, item?.classification),
        tone: pickField(item?.tone, item?.sentimentTone),
        score: Number(item?.sentimentScore ?? item?.score ?? 0),
        concern: concernChips.length > 0 ? concernChips.join(", ") : "-",
        concernChips,
        date: dateStr,
        time: timeStr,
        dateRaw: hasValidTimestamp ? parsedTimestamp.toISOString() : null,
        timestampRaw: pickField(
          item?.timestamp,
          item?.processedAt,
          item?.createdAt,
          item?.recordedAt,
          item?.submittedAt,
          item?.surveyDateTime,
          item?.responseDateTime,
          item?.dateTime,
        ),
        processedAtRaw: pickField(item?.processedAt, item?.processed_at),
        createdAtRaw: pickField(item?.createdAt, item?.created_at),
        updatedAtRaw: pickField(item?.updatedAt, item?.updated_at),
        deletedAtRaw: pickField(item?.deletedAt, item?.deleted_at),
        ageGroup: pickField(
          item?.ageGroup,
          item?.age_group,
          item?.ageBracket,
          item?.age_bracket,
          item?.demographics?.ageGroup,
          item?.demographics?.ageBracket,
          derivedAgeGroup,
        ),
        age: pickField(item?.age, item?.respondentAge, item?.demographics?.age, item?.ageYears),
        sex: pickField(item?.sex, item?.gender, item?.demographics?.gender, item?.respondentSex),
        occupation: pickField(item?.occupation, item?.occupationType, item?.jobTitle),
        employmentSector: pickField(
          item?.employmentSector,
          item?.employment_sector,
          item?.employment,
          item?.occupationSector,
        ),
        residency: pickField(
          item?.residency,
          item?.yearsInArea,
          item?.years_in_area,
          item?.yearsOfResidency,
          item?.residencyYears,
        ),
        summary: pickField(item?.summary, item?.briefSummary),
        transcript: pickField(item?.transcript, item?.transcription),
        audioUrl: pickField(item?.audioUrl, item?.audio_url, item?.recordingUrl, item?.audio),
        respondentAudioUrl: pickField(item?.respondentAudioUrl, item?.respondent_audio_url),
        respondentName: pickField(item?.respondentName, item?.respondent, item?.name),
        userId: pickField(item?.userId, item?.user_id),
        speakerRoleMap: pickField(item?.speakerRoleMap, item?.speaker_role_map),
        campaignCode: pickField(item?.campaignCode, item?.campaign_code) || c.code,
        civilStatus: pickField(
          item?.civilStatus,
          item?.civil_status,
          item?.maritalStatus,
          item?.marital_status,
        ),
        dependents: pickField(
          item?.dependents,
          item?.numberOfDependents,
          item?.noOfDependents,
          item?.householdDependents,
        ),
        yearsInArea: pickField(
          item?.yearsInArea,
          item?.years_in_area,
          item?.yearsOfResidency,
          item?.residencyYears,
          item?.residency,
        ),
        areaType: pickField(
          item?.areaType,
          item?.area_type,
          item?.areaClassification,
          item?.urbanRural,
        ),
        socialClass: pickField(
          item?.socialClass,
          item?.social_class,
          item?.socioEconomicClass,
          item?.socio_economic_class,
          item?.secClass,
        ),
        monthlyIncome: pickField(
          item?.monthlyIncome,
          item?.monthly_income,
          item?.incomeBracket,
          item?.monthly_income_bracket,
        ),
        housing: pickField(
          item?.housing,
          item?.housingType,
          item?.housing_type,
          item?.residenceType,
        ),
        educationAttainment: pickField(
          item?.educationAttainment,
          item?.educationalAttainment,
          item?.education,
          item?.highestEducation,
        ),
        fieldOfStudy: pickField(item?.fieldOfStudy, item?.field_of_study, item?.course),
        primaryPlatform: pickField(
          item?.primaryPlatform,
          item?.primaryMedia,
          item?.mediaSource,
          item?.media,
          item?.sourceOfInfo,
        ),
        religion: pickField(item?.religion, item?.religiousAffiliation),
        ethnicityLanguage: pickField(
          item?.ethnicityLanguage,
          item?.ethnicity,
          item?.language,
          item?.ethnicity_or_language,
        ),
        voterStatus: pickField(item?.voterStatus, item?.voter_status),
        voteIntent: pickField(item?.voteIntent, item?.vote_intent),
        voteIntentStrength: pickField(
          item?.voteIntentStrength,
          item?.vote_intent_strength,
          item?.intentStrength,
        ),
        nationalGov: pickField(item?.nationalGov, item?.national_gov, item?.nationalGovRating),
        governor: pickField(item?.governor, item?.localGovGovernor, item?.governorRating),
        mayor: pickField(item?.mayor, item?.localGovMayor, item?.mayorRating),
        coreValues: pickField(item?.coreValues, item?.core_values, item?.leaderCoreValues),
        leaderArchetype: pickField(
          item?.leaderArchetype,
          item?.leader_archetype,
          item?.archetypePreference,
        ),
        desiredTraits,
        rejectedTraits,
      };
    });
    const uiRecordings = apiRecordings.length > 0 ? apiRecordings : d.recordings;
    const recsFilterGroups = RECORDING_FILTER_DEFS.map((def) => {
      const options =
        def.key === "enumerator" && recordingsEnumeratorOptions.length > 0
          ? recordingsEnumeratorOptions
          : Array.from(
              uiRecordings
                .reduce((counts, rec) => {
                  const value = def.getValue(rec);
                  if (!value) return counts;
                  counts.set(value, (counts.get(value) || 0) + 1);
                  return counts;
                }, new Map())
                .entries(),
            )
              .map(([value, count]) => ({
                value,
                label: def.formatLabel ? def.formatLabel(value) : value,
                count,
              }))
              .sort((a, b) => {
                if (Array.isArray(def.sortOrder)) {
                  const aIdx = def.sortOrder.indexOf(a.value);
                  const bIdx = def.sortOrder.indexOf(b.value);
                  const safeA = aIdx < 0 ? Number.MAX_SAFE_INTEGER : aIdx;
                  const safeB = bIdx < 0 ? Number.MAX_SAFE_INTEGER : bIdx;
                  if (safeA !== safeB) return safeA - safeB;
                }
                if (b.count !== a.count) return b.count - a.count;
                return String(a.label).localeCompare(String(b.label));
              });

      return { ...def, options };
    });

    const filteredRecordings = uiRecordings.filter((rec) =>
      RECORDING_FILTER_DEFS.every((def) => {
        const selected = recsFiltersApplied[def.key] || [];
        if (selected.length === 0) return true;
        const value = def.getValue(rec);
        return value != null && selected.includes(value);
      }),
    );

    const exportRecordingsAsJson = () => {
      const payload = filteredRecordings.map((rec) => {
        const exportedTimestamp =
          rec.timestampRaw || formatExportDateTime(rec.dateRaw) || formatExportDateTime(rec.date);
        const parsedProcessedId = Number(rec.fieldResponseProcessedId ?? rec.id);
        const parsedFieldResponseId = Number(
          rec.fieldResponseProcessedId ?? rec.fieldResponseId ?? rec.id,
        );
        const parsedAge = Number(rec.age);
        const normalizedSpeakerRoleMap =
          rec.speakerRoleMap == null
            ? "{}"
            : typeof rec.speakerRoleMap === "string"
              ? rec.speakerRoleMap
              : JSON.stringify(rec.speakerRoleMap);

        return {
          field_response_processed_id: Number.isFinite(parsedProcessedId)
            ? parsedProcessedId
            : null,
          field_response_id: Number.isFinite(parsedFieldResponseId) ? parsedFieldResponseId : null,
          transcript: rec.transcript || "",
          sentiment: rec.sentiment || "neutral",
          category: rec.category || "other",
          employment_sector: rec.employmentSector || "unknown",
          tone: rec.tone || "neutral",
          summary: rec.summary || "",
          key_concerns: rec.concern && rec.concern !== "-" ? rec.concern : "none identified",
          age: Number.isFinite(parsedAge) ? parsedAge : null,
          sex: rec.sex || null,
          timestamp: exportedTimestamp,
          processed_at: rec.processedAtRaw || exportedTimestamp,
          latitude: Number.isFinite(rec.latitude) ? rec.latitude : null,
          longitude: Number.isFinite(rec.longitude) ? rec.longitude : null,
          address: rec.location || null,
          user_id: rec.userId || null,
          name: rec.enumerator || null,
          campaign_code: rec.campaignCode || null,
          audio_url: rec.audioUrl || null,
          respondent_audio_url: rec.respondentAudioUrl || null,
          created_at: rec.createdAtRaw || exportedTimestamp,
          updated_at: rec.updatedAtRaw || rec.createdAtRaw || exportedTimestamp,
          deleted_at: rec.deletedAtRaw || null,
          speaker_role_map: normalizedSpeakerRoleMap,
          identified_barangay: rec.identifiedBarangay || rec.barangay || null,
        };
      });

      const campaignCode = c.code || "recordings";
      const stamp = new Date().toISOString().replace(/[.:]/g, "-");
      const filename = `${campaignCode}_recordings_export_${stamp}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(downloadUrl);
    };

    const activeFilterChips = recsFilterGroups.flatMap((group) => {
      const selected = recsFiltersApplied[group.key] || [];
      return selected.map((value) => {
        const option = group.options.find((item) => item.value === value);
        return {
          key: group.key,
          value,
          title: group.title,
          valueLabel: option?.label || value,
        };
      });
    });

    const activeFilterCount = activeFilterChips.length;

    const recordingsPerPage = RECORDINGS_UI_PER_PAGE;
    const totalRecordingsPages = Math.max(
      1,
      Math.ceil(filteredRecordings.length / recordingsPerPage),
    );
    const currentRecordingsPage = Math.min(recordingsPage, totalRecordingsPages);
    const paginatedRecordings = filteredRecordings.slice(
      (currentRecordingsPage - 1) * recordingsPerPage,
      currentRecordingsPage * recordingsPerPage,
    );
    const canGoToNextPage = currentRecordingsPage < totalRecordingsPages;
    const recordingsTotal =
      resolveCount(processedResponses, ["count", "total", "totalCount", "total_count"]) ||
      totalProcessed ||
      d.totalRecordings;
    const mapRecordings = filteredRecordings.filter(
      (rec) => Number.isFinite(rec.latitude) && Number.isFinite(rec.longitude),
    );
    const googleMapsApiKey = env.GOOGLE_MAP_API_KEY || "";
    const mapCenter = mapRecordings.length
      ? { lat: mapRecordings[0].latitude, lng: mapRecordings[0].longitude }
      : { lat: 14.2167, lng: 121.1653 };
    const positiveCount = analytics ? resolvedSentiment.positive : c.pos;
    const negativeCount = analytics ? resolvedSentiment.negative : c.neg;
    const neutralCount = analytics ? resolvedSentiment.neutral : c.neutral;
    const enumeratorPerformanceEntries = analytics
      ? resolveEnumeratorPerformanceEntries(analytics?.enumeratorPerformance)
      : d.enumeratorPerformance;
    const enumeratorPerformanceMax = Math.max(
      ...enumeratorPerformanceEntries.map((item) => Number(item?.count || 0)),
      1,
    );
    const selectedPinName =
      selectedMapPin?.respondentName || selectedMapPin?.name || "Response Details";
    const selectedPinSentiment = (selectedMapPin?.sentiment || "neutral").toLowerCase();
    const selectedPinLat = Number(selectedMapPin?.latitude);
    const selectedPinLng = Number(selectedMapPin?.longitude);
    const selectedPinTone = selectedMapPin?.tone || "";
    const selectedPinConcerns = Array.isArray(selectedMapPin?.concernChips)
      ? selectedMapPin.concernChips
      : Array.isArray(selectedMapPin?.concerns)
        ? selectedMapPin.concerns
        : String(selectedMapPin?.concern || "")
            .split(/,|;/)
            .map((v) => v.trim())
            .filter(Boolean);
    const selectedPinAgeRange =
      selectedMapPin?.demographics?.ageRange || selectedMapPin?.ageGroup || "";
    const selectedPinGender = selectedMapPin?.demographics?.gender || selectedMapPin?.sex || "";
    const selectedPinOccupation =
      selectedMapPin?.demographics?.occupation || selectedMapPin?.occupation || "";
    const selectedPinYearsInArea =
      selectedMapPin?.demographics?.yearsInArea || selectedMapPin?.yearsInArea || "";
    const selectedPinSocialClass =
      selectedMapPin?.demographics?.socialClass || selectedMapPin?.socialClass || "";
    const selectedPinVoterType =
      selectedMapPin?.political?.voterType || selectedMapPin?.voterStatus || "";
    const selectedPinIntent = selectedMapPin?.political?.intent || selectedMapPin?.voteIntent || "";
    const selectedPinNatGov =
      selectedMapPin?.political?.natGov || selectedMapPin?.nationalGov || "";
    const selectedPinMayor = selectedMapPin?.political?.mayor || selectedMapPin?.mayor || "";
    const selectedPinDesiredTraits = Array.isArray(selectedMapPin?.traits?.desirable)
      ? selectedMapPin.traits.desirable
      : Array.isArray(selectedMapPin?.desiredTraits)
        ? selectedMapPin.desiredTraits
        : [];
    const selectedPinRejectedTraits = Array.isArray(selectedMapPin?.traits?.rejected)
      ? selectedMapPin.traits.rejected
      : Array.isArray(selectedMapPin?.rejectedTraits)
        ? selectedMapPin.rejectedTraits
        : [];
    const categoryEntries = resolveDistributionEntries(analytics?.category).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const employmentSectorEntries = resolveDistributionEntries(analytics?.employment).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const keyConcernEntries = resolveDistributionEntries(analytics?.concerns).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const renderAdditionalDistributionCard = (title, entries, color) => {
      const data = entries.slice(0, 6);
      const maxCount = getMaxEntryCount(data);

      return (
        <div className={styles.card}>
          <div className={styles.sh2} style={{ fontSize: 15, marginBottom: 12 }}>
            {title}
          </div>
          {data.length > 0 ? (
            <div className={styles.adList}>
              {data.map((item) => (
                <div key={`${title}-${item.label}`} className={styles.adRow}>
                  <div className={styles.adLabel}>{item.label}</div>
                  <div className={styles.adTrack}>
                    <div
                      className={styles.adFill}
                      style={{
                        width: `${getPercent(item.count, maxCount)}%`,
                        background: color,
                      }}
                    />
                  </div>
                  <div className={styles.adCount}>{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.infoValMuted}>No data available</div>
          )}
        </div>
      );
    };

    return (
      <div className={styles.page}>
        {analyticsLoading && (
          <div className={styles.loadingOverlay} role="status" aria-live="polite">
            <div className={styles.loadingOverlayCard}>
              <div className={styles.loadingSpinner} aria-hidden="true" />
              <div className={styles.loadingText}>Loading campaign analytics...</div>
            </div>
          </div>
        )}

        {/* Banner */}
        <div className={styles.campBanner}>
          <div className={styles.breadcrumb}>
            <button type="button" className={styles.breadcrumbLink} onClick={goBack}>
              My Campaigns
            </button>
            <svg
              viewBox="0 0 24 24"
              strokeWidth="2"
              fill="none"
              stroke="currentColor"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className={styles.breadcrumbCurrent}>{c.code}</span>
          </div>
          <div className={styles.campBannerBody}>
            <div className={styles.campCodeBadge}>{c.code}</div>
            <div className={styles.campBannerInfo}>
              <div className={styles.campBannerName}>{c.title}</div>
              <div className={styles.campBannerMeta}>
                <span>{c.location}</span>
                <span className={styles.bannerDot}>·</span>
                <span>{c.date}</span>
                <span className={styles.bannerDot}>·</span>
                <div className={styles.campBannerStatus}>
                  <div className={styles.statusDot} style={{ background: statusColor }} />
                  <span>{c.status}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.htabStrip}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={
                  activeTab === tab.id ? `${styles.htab} ${styles.htabActive}` : styles.htab
                }
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Panels */}
        <div className={styles.tabPanels}>
          {/* TAB: Campaign Info */}
          {activeTab === "info" && (
            <div>
              {analyticsLoading && <div className={styles.analyticsLoader}>Loading analytics…</div>}
              {analyticsError && <div className={styles.analyticsError}>{analyticsError}</div>}
              {!campaignTypeLoading && campaignType === "custom_survey" && (
                <div className={styles.surveyGuidePanel}>
                  <div className={styles.surveyGuidePanelIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4H19v14.5A1.5 1.5 0 0 0 17.5 17H7.75A2.75 2.75 0 0 0 5 19.75V6.5Z" />
                      <path d="M7.5 4A2.5 2.5 0 0 0 5 6.5V20l2.1-1.4a3 3 0 0 1 1.67-.5H17.5" />
                    </svg>
                  </div>
                  <div className={styles.surveyGuidePanelContent}>
                    <div className={styles.surveyGuidePanelTitleRow}>
                      <div className={styles.surveyGuidePanelTitle}>Custom Survey</div>
                      <span className={styles.surveyGuidePanelBadge}>Guided</span>
                    </div>
                    <div className={styles.surveyGuidePanelMeta}>{getSurveyGuideBannerMeta(c)}</div>
                  </div>
                  <button
                    type="button"
                    className={styles.surveyGuidePanelButton}
                    onClick={() => onOpenSurveyBuilder(c)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 5h5v5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 14 19 5" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M19 13v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    View / Edit Survey Guide
                  </button>
                </div>
              )}
              <div className={styles.infoGrid}>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Code</div>
                  <div className={styles.infoValMono}>{c.code}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Name</div>
                  <div className={styles.infoValLg}>{c.title}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Status</div>
                  <div className={styles.infoVal} style={{ marginTop: 4 }}>
                    <span
                      className={styles.statusPill}
                      style={{
                        borderColor: `${statusColor}55`,
                        background: `${statusColor}22`,
                        color: statusColor,
                      }}
                    >
                      {c.status}
                    </span>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Start Date</div>
                  <div className={styles.infoVal}>{c.date}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Survey Type</div>
                  <div className={styles.infoVal}>{getSurveyTypeLabel(c.surveyType)}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Location</div>
                  <div className={styles.infoVal}>{c.location}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Total Recordings</div>
                  <div className={styles.infoValNum}>{recordingsTotal}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Sentiment Breakdown</div>
                  <div className={styles.infoVal} style={{ marginTop: 6 }}>
                    <div style={{ color: "#34d399", fontWeight: 600 }}>
                      {positiveCount} positive
                    </div>
                    <div style={{ color: "#f87171", fontWeight: 600 }}>
                      {negativeCount} negative
                    </div>
                    <div style={{ color: "#fbbf24", fontWeight: 600 }}>{neutralCount} neutral</div>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Executive Summary</div>
                  <div className={styles.infoVal} style={{ marginTop: 4 }}>
                    <span style={{ color: "rgba(191,219,254,0.4)", fontSize: 12 }}>
                      Not yet generated
                    </span>
                    <br />
                    <span style={{ color: "rgba(191,219,254,0.3)", fontSize: 11 }}>
                      End campaign to generate
                    </span>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Survey Document</div>
                  {c.surveyQuestionDocument ? (
                    <a
                      href={c.surveyQuestionDocument}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.infoVal}
                    >
                      View document
                    </a>
                  ) : (
                    <div className={styles.infoValMuted}>No document uploaded</div>
                  )}
                </div>
              </div>

              <div className={styles.twoCol}>
                <div className={styles.card}>
                  <div className={styles.sh2}>Enumerator Performance</div>
                  <div className={styles.enumList}>
                    {enumeratorPerformanceEntries.map((en) => (
                      <div key={en.name} className={styles.enumRow}>
                        <div className={styles.enumAva}>{en.initials}</div>
                        <div className={styles.enumName}>{en.name}</div>
                        <div className={styles.enumBt}>
                          <div
                            className={styles.enumBf}
                            style={{ width: `${getPercent(en.count, enumeratorPerformanceMax)}%` }}
                          />
                        </div>
                        <div className={styles.enumCt}>{en.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.sh2}>Age Distribution</div>
                  <div className={styles.distList}>
                    {(() => {
                      const entries = analytics
                        ? ageEntries
                        : resolveAgeDistributionEntries(d.ageDistribution);
                      const maxCount = Math.max(...entries.map((e) => e.count), 1);
                      return entries.map((item) => (
                        <div key={item.label} className={styles.distRow}>
                          <div className={styles.distLabel}>{item.label}</div>
                          <div className={styles.distBarTrack}>
                            <div
                              className={styles.distBarFill}
                              style={{ width: `${Math.round((item.count / maxCount) * 100)}%` }}
                            />
                          </div>
                          <div className={styles.distCount}>{item.count}</div>
                        </div>
                      ));
                    })()}
                  </div>
                  <div style={{ marginTop: 20 }}>
                    <div className={styles.sh2} style={{ fontSize: 15, marginBottom: 12 }}>
                      Gender Distribution
                    </div>
                    <div className={styles.genderRow}>
                      {(analytics ? sexEntries : d.genderDistribution).map((item) => (
                        <div key={item.label} className={styles.genderItem}>
                          <div className={styles.genderNum}>{item.count}</div>
                          <div className={styles.genderLbl}>{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.sh2}>Additional Demographics</div>
              <div className={styles.threeCol}>
                {renderAdditionalDistributionCard("Category", categoryEntries, "#3b82f6")}
                {renderAdditionalDistributionCard(
                  "Employment Sector",
                  employmentSectorEntries,
                  "#10b981",
                )}
                {renderAdditionalDistributionCard("Key Concerns", keyConcernEntries, "#f59e0b")}
              </div>
            </div>
          )}

          {/* TAB: Executive Summary */}
          {activeTab === "exec" && (
            <div>
              <div className={styles.execContent}>
                <div className={styles.execEmpty}>
                  <div className={styles.execEmptyIcon}>
                    <svg viewBox="0 0 24 24" strokeWidth="1.5" fill="none" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div className={styles.execEmptyTitle}>No Executive Summary yet</div>
                  <div className={styles.execEmptySub}>
                    The executive summary is generated when you end the campaign. Go to{" "}
                    <strong>My Campaigns</strong> and click <strong>End</strong> on{" "}
                    <strong>{c.code}</strong> to generate it.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: Map View */}
          {activeTab === "map" && (
            <div>
              <div
                className={`${styles.filterBar} ${
                  activeFilterCount > 0 ? styles.filterBarActive : ""
                }`}
              >
                <button
                  type="button"
                  className={`${styles.btnFilter} ${
                    activeFilterCount > 0 ? styles.btnFilterActive : ""
                  }`}
                  onClick={openRecsFilterPanel}
                >
                  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="20" y2="12" />
                    <line x1="12" y1="18" x2="20" y2="18" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className={styles.filterBadge}>{activeFilterCount}</span>
                  )}
                </button>
                <div className={styles.recsActiveChips}>
                  {activeFilterChips.map((chip) => (
                    <span key={`${chip.key}-${chip.value}`} className={styles.filterChip}>
                      {chip.title}: <strong>{chip.valueLabel}</strong>
                    </span>
                  ))}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    className={styles.btnClearAll}
                    onClick={clearAllRecsFilters}
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className={styles.mapLeg}>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#10b981" }} />
                  Positive
                </div>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#f59e0b" }} />
                  Neutral / Mixed
                </div>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#ef4444" }} />
                  Negative
                </div>
              </div>
              <div className={styles.mapGrid}>
                <div className={styles.mapCanvasWrap}>
                  <div className={styles.mapLblBox}>
                    <div className={styles.mapLblT}>Laguna Province</div>
                    <div className={styles.mapLblB}>Survey Coverage</div>
                  </div>
                  {googleMapsApiKey ? (
                    <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                      <GoogleMap
                        onLoad={(map) => {
                          mapRef.current = map;
                        }}
                        onClick={() => setSelectedMapPin(null)}
                        onUnmount={() => {
                          mapRef.current = null;
                        }}
                        mapContainerClassName={styles.mapGoogleCanvas}
                        center={mapCenter}
                        zoom={10}
                        options={{
                          disableDefaultUI: true,
                          zoomControl: true,
                          styles: [
                            { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
                            { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
                            {
                              elementType: "labels.text.stroke",
                              stylers: [{ color: "#0f172a" }],
                            },
                            {
                              featureType: "road",
                              elementType: "geometry",
                              stylers: [{ color: "#1e293b" }],
                            },
                            {
                              featureType: "road",
                              elementType: "labels",
                              stylers: [{ visibility: "off" }],
                            },
                            {
                              featureType: "poi",
                              stylers: [{ visibility: "off" }],
                            },
                            {
                              featureType: "water",
                              elementType: "geometry",
                              stylers: [{ color: "#0b2443" }],
                            },
                          ],
                        }}
                      >
                        {mapRecordings.map((rec) => (
                          <Marker
                            key={rec.id}
                            position={{ lat: rec.latitude, lng: rec.longitude }}
                            title={`${rec.location} (${rec.sentiment})`}
                            icon={{
                              url: mapPinIconUrl(rec.sentiment, selectedMapPin?.id === rec.id),
                            }}
                            onClick={() => {
                              setSelectedMapPin(rec);
                              if (mapRef.current) {
                                mapRef.current.panTo({ lat: rec.latitude, lng: rec.longitude });
                                const currentZoom = Number(mapRef.current.getZoom?.() ?? 10);
                                mapRef.current.setZoom(Math.max(currentZoom, 13));
                              }
                            }}
                          />
                        ))}
                      </GoogleMap>
                    </LoadScriptNext>
                  ) : (
                    <div className={styles.mapApiNotice}>
                      Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> to enable Google Map.
                    </div>
                  )}
                </div>
                <div className={styles.mapSide}>
                  {selectedMapPin ? (
                    <div className={styles.mapPinDetails}>
                      <div className={styles.mapPinHeader}>
                        <div className={styles.mapPinTitle}>{selectedPinName}</div>
                        <button
                          className={styles.mapPinClose}
                          onClick={() => setSelectedMapPin(null)}
                          aria-label="Close"
                        >
                          ×
                        </button>
                      </div>
                      <div className={styles.mapPinContent}>
                        {/* Location */}
                        <div className={styles.mapPinField}>
                          <div className={styles.mapPinLabel}>Location</div>
                          <div className={styles.mapPinValue}>
                            {selectedMapPin.location}
                            <br />
                            <span style={{ fontSize: "11px", color: "rgba(191, 219, 254, 0.6)" }}>
                              {Number.isFinite(selectedPinLat) && Number.isFinite(selectedPinLng)
                                ? `${selectedPinLat.toFixed(4)}, ${selectedPinLng.toFixed(4)}`
                                : "-"}
                            </span>
                          </div>
                        </div>

                        {/* Date */}
                        {selectedMapPin.date && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Date</div>
                            <div className={styles.mapPinValue}>{selectedMapPin.date}</div>
                          </div>
                        )}

                        {/* Enumerator */}
                        {selectedMapPin.enumerator && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Enumerator</div>
                            <div className={styles.mapPinValue}>{selectedMapPin.enumerator}</div>
                          </div>
                        )}

                        {/* Sentiment & Tone */}
                        <div className={styles.mapPinField}>
                          <div className={styles.mapPinLabel}>Sentiment & Tone</div>
                          <div className={styles.mapPinValue}>
                            <span
                              className={`${styles.badge} ${
                                selectedPinSentiment === "negative"
                                  ? styles.badgeNegative
                                  : selectedPinSentiment === "positive"
                                    ? styles.badgePositive
                                    : styles.badgeNeutral
                              }`}
                            >
                              {selectedPinSentiment.toUpperCase()}
                            </span>
                            {selectedPinTone && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "rgba(191, 219, 254, 0.7)",
                                  marginLeft: "8px",
                                }}
                              >
                                {selectedPinTone}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Key Concerns */}
                        {selectedPinConcerns.length > 0 && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Key Concerns</div>
                            <div className={styles.mapPinBadgeGroup}>
                              {selectedPinConcerns.map((concern, idx) => (
                                <span
                                  key={idx}
                                  className={`${styles.badge} ${styles.badgeConcern}`}
                                >
                                  {concern}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Summary */}
                        {selectedMapPin.summary && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Summary</div>
                            <div
                              className={styles.mapPinValue}
                              style={{ fontSize: "12px", lineHeight: "1.6" }}
                            >
                              {selectedMapPin.summary}
                            </div>
                          </div>
                        )}

                        {/* Demographics */}
                        {(selectedPinAgeRange || selectedPinGender || selectedPinOccupation) && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Demographics</div>
                            <div className={styles.mapPinValue} style={{ fontSize: "12px" }}>
                              {[selectedPinAgeRange, selectedPinGender, selectedPinOccupation]
                                .filter(Boolean)
                                .join(" · ")}
                              {(selectedPinYearsInArea || selectedPinSocialClass) && (
                                <>
                                  <br />
                                  {[
                                    selectedPinYearsInArea &&
                                      `Yrs in area: ${selectedPinYearsInArea}`,
                                    selectedPinSocialClass && `Class: ${selectedPinSocialClass}`,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Political Profile */}
                        {(selectedPinVoterType ||
                          selectedPinIntent ||
                          selectedPinNatGov ||
                          selectedPinMayor) && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Political Profile</div>
                            <div className={styles.mapPinValue} style={{ fontSize: "12px" }}>
                              {[selectedPinVoterType, selectedPinIntent]
                                .filter(Boolean)
                                .join(" · ")}
                              {(selectedPinNatGov || selectedPinMayor) && (
                                <>
                                  <br />
                                  {[
                                    selectedPinNatGov && `Nat. gov: ${selectedPinNatGov}`,
                                    selectedPinMayor && `Mayor: ${selectedPinMayor}`,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Leader Preferences */}
                        {(selectedPinDesiredTraits.length > 0 ||
                          selectedPinRejectedTraits.length > 0) && (
                          <div className={styles.mapPinField}>
                            <div className={styles.mapPinLabel}>Leader Preferences</div>
                            <div className={styles.mapPinTraitGroup}>
                              {selectedPinDesiredTraits.map((trait, idx) => (
                                <span key={`d-${idx}`} className={styles.traitDesirable}>
                                  {trait}
                                </span>
                              ))}
                              {selectedPinRejectedTraits.map((trait, idx) => (
                                <span key={`r-${idx}`} className={styles.traitRejected}>
                                  {trait}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.mapPh}>
                      <svg viewBox="0 0 24 24" strokeWidth="1.5" fill="none" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <p>
                        Markers represent processed responses with coordinates.
                        {mapRecordings.length > 0
                          ? ` Showing ${mapRecordings.length} plotted points.`
                          : " No coordinates available for this campaign yet."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB: Recordings */}
          {activeTab === "recs" && (
            <div className={styles.recsWrap}>
              <div className={styles.rkRow}>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Total</div>
                  <div className={styles.rkVal}>{recordingsTotal}</div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Positive</div>
                  <div className={styles.rkVal} style={{ color: "#34d399" }}>
                    {positiveCount}
                  </div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Negative</div>
                  <div className={styles.rkVal} style={{ color: "#f87171" }}>
                    {negativeCount}
                  </div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Mixed/Neutral</div>
                  <div className={styles.rkVal} style={{ color: "#fbbf24" }}>
                    {neutralCount}
                  </div>
                </div>
              </div>

              <div
                className={`${styles.filterBar} ${
                  activeFilterCount > 0 ? styles.filterBarActive : ""
                }`}
              >
                <button
                  type="button"
                  className={`${styles.btnFilter} ${
                    activeFilterCount > 0 ? styles.btnFilterActive : ""
                  }`}
                  onClick={openRecsFilterPanel}
                >
                  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="20" y2="12" />
                    <line x1="12" y1="18" x2="20" y2="18" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className={styles.filterBadge}>{activeFilterCount}</span>
                  )}
                </button>
                <div className={styles.recsActiveChips}>
                  {activeFilterChips.map((chip) => (
                    <span key={`${chip.key}-${chip.value}`} className={styles.filterChip}>
                      {chip.title}: <strong>{chip.valueLabel}</strong>
                    </span>
                  ))}
                </div>
                <div className={styles.filterActionsRight}>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      className={styles.btnClearAll}
                      onClick={clearAllRecsFilters}
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnFilter}
                    onClick={exportRecordingsAsJson}
                  >
                    <svg viewBox="0 0 24 24" strokeWidth="1.8" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v9" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.5 10.5 3.5 3.5 3.5-3.5"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 18h14" />
                    </svg>
                    Export JSON
                  </button>
                </div>
              </div>

              <div className={styles.recList}>
                <audio
                  ref={listAudioRef}
                  style={{ display: "none" }}
                  onPlay={() => setInlineAudioPlaying(true)}
                  onPause={() => setInlineAudioPlaying(false)}
                  onEnded={() => {
                    setInlineAudioPlaying(false);
                    setInlineAudioCurrent(0);
                  }}
                  onTimeUpdate={(e) => setInlineAudioCurrent(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => setInlineAudioDuration(e.currentTarget.duration)}
                />
                {isRecordingsPageLoading && (
                  <div className={styles.recEmpty}>Loading recordings...</div>
                )}
                {!isRecordingsPageLoading && paginatedRecordings.length === 0 && (
                  <div className={styles.recEmpty}>No recordings match the selected filters.</div>
                )}
                {paginatedRecordings.map((rec) => (
                  <div key={rec.id} className={styles.rlItem}>
                    <div style={{ marginBottom: 10 }}>
                      <div className={styles.rlLbl}>
                        {formatRecordIdLabel(rec.fieldResponseProcessedId)}
                      </div>
                    </div>
                    <div className={styles.rlGrid}>
                      {/* Location */}
                      <div>
                        <div className={styles.rlLbl}>Location</div>
                        <div className={styles.rlVal}>{rec.location}</div>
                        {rec.coordsStr && (
                          <div
                            className={styles.rlValSm}
                            style={{
                              fontFamily: "ui-monospace, monospace",
                              marginTop: 2,
                              fontSize: 11,
                            }}
                          >
                            {rec.coordsStr}
                          </div>
                        )}
                      </div>
                      {/* Date */}
                      <div>
                        <div className={styles.rlLbl}>Date</div>
                        <div className={styles.rlValSm}>{rec.date}</div>
                        {rec.time && <div className={styles.rlValSm}>{rec.time}</div>}
                      </div>
                      {/* Demographics */}
                      <div>
                        <div className={styles.rlLbl}>Demographics</div>
                        {rec.ageGroup && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Age Range:</span> {rec.ageGroup}
                          </div>
                        )}
                        {rec.sex && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Gender:</span> {rec.sex}
                          </div>
                        )}
                        {(rec.employmentSector || rec.occupation) && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Employment Sector:</span>{" "}
                            {rec.employmentSector || rec.occupation}
                          </div>
                        )}
                        {(rec.residency || rec.areaType) && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Residence:</span>{" "}
                            {rec.residency || rec.areaType}
                          </div>
                        )}
                        {rec.enumerator && (
                          <div className={styles.rlValSm} style={{ marginTop: 4 }}>
                            <span style={{ color: "#60a5fa" }}>By:</span> {rec.enumerator}
                          </div>
                        )}
                      </div>
                      {/* Summary */}
                      <div>
                        <div className={styles.rlLbl}>Summary</div>
                        <div className={styles.rlValSm} style={{ lineHeight: 1.6 }}>
                          {rec.summary || "—"}
                        </div>
                      </div>
                      {/* Sentiment */}
                      <div>
                        <div className={styles.rlLbl}>Sentiment</div>
                        <span
                          className={styles.recSentBadge}
                          style={{
                            borderColor: `${sentimentColor(rec.sentiment)}55`,
                            background: `${sentimentColor(rec.sentiment)}22`,
                            color: sentimentColor(rec.sentiment),
                          }}
                        >
                          {rec.sentiment}
                        </span>
                        {rec.tone && (
                          <div className={styles.rlValSm} style={{ marginTop: 4 }}>
                            <span style={{ color: "#60a5fa" }}>Tone:</span> {rec.tone}
                          </div>
                        )}
                        <div className={styles.recActs}>
                          <button
                            type="button"
                            className={styles.btnView}
                            onClick={() => setSelectedRecording(rec)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            View
                          </button>
                          <button
                            type="button"
                            className={styles.btnDel}
                            onClick={() => onOpenDeleteRecordingModal(rec)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={styles.inlineAudio}>
                      <button
                        type="button"
                        className={styles.iPlayBtn}
                        onClick={() => toggleInlinePlay(rec)}
                        disabled={!rec.audioUrl}
                        aria-label={
                          inlineAudioPlaying && inlineAudioRecordId === rec.id ? "Pause" : "Play"
                        }
                      >
                        {inlineAudioPlaying && inlineAudioRecordId === rec.id ? (
                          <svg viewBox="0 0 24 24" fill="white">
                            <rect x="5" y="3" width="4" height="18" rx="1" />
                            <rect x="15" y="3" width="4" height="18" rx="1" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="white">
                            <polygon points="5,3 19,12 5,21" />
                          </svg>
                        )}
                      </button>
                      <div className={styles.iProgBar} onClick={(e) => seekInlineAudio(e, rec.id)}>
                        <div
                          className={styles.iProgFill}
                          style={{
                            width:
                              inlineAudioRecordId === rec.id && inlineAudioDuration > 0
                                ? `${(inlineAudioCurrent / inlineAudioDuration) * 100}%`
                                : "0%",
                          }}
                        />
                      </div>
                      <div className={styles.iDur}>
                        {inlineAudioRecordId === rec.id
                          ? `${formatAudioTime(inlineAudioCurrent)} / ${formatAudioTime(inlineAudioDuration)}`
                          : "0:00 / 0:00"}
                      </div>
                      <button
                        type="button"
                        className={styles.iStopBtn}
                        title="Stop & restart"
                        onClick={stopInlinePlay}
                        disabled={inlineAudioRecordId !== rec.id}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="4" y="4" width="16" height="16" rx="2" />
                        </svg>
                      </button>
                    </div>
                    {rec.concernChips.length > 0 && (
                      <div className={styles.kcChips}>
                        {rec.concernChips.map((chip) => (
                          <span key={chip} className={styles.kcChip}>
                            {chip}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.recPagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setRecordingsPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentRecordingsPage <= 1 || isRecordingsPageLoading}
                >
                  Previous
                </button>
                <div className={styles.pageInfo}>
                  {isRecordingsPageLoading
                    ? `Loading page ${currentRecordingsPage}...`
                    : `Page ${currentRecordingsPage} of ${totalRecordingsPages}`}
                </div>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() =>
                    setRecordingsPage((prev) => Math.min(totalRecordingsPages, prev + 1))
                  }
                  disabled={!canGoToNextPage || isRecordingsPageLoading}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {isRecsFilterOpen && (
            <button
              type="button"
              className={styles.fpBackdrop}
              aria-label="Close filters"
              onClick={closeRecsFilterPanel}
            />
          )}

          <div className={`${styles.fpPanel} ${isRecsFilterOpen ? styles.fpPanelOpen : ""}`}>
            <div className={styles.fpHead}>
              <div className={styles.fpTitle}>Filter Recordings</div>
              <button type="button" className={styles.fpClose} onClick={closeRecsFilterPanel}>
                ×
              </button>
            </div>
            <div className={styles.fpBody}>
              {recsFilterGroups.map((group) => {
                const selected = recsFiltersDraft[group.key] || [];
                const isAllChecked = selected.length === 0;

                return (
                  <div key={group.key} className={styles.fpGroup}>
                    <div className={styles.fpGroupTitle}>{group.title}</div>
                    <div className={styles.fpOptions}>
                      <label className={`${styles.fpOpt} ${styles.fpAllRow}`}>
                        <input
                          type="checkbox"
                          checked={isAllChecked}
                          onChange={() => toggleRecsDraftFilter(group.key, "__all__")}
                        />
                        <span className={styles.fpOptLbl}>All</span>
                      </label>
                      {group.options.map((option) => (
                        <label key={option.value} className={styles.fpOpt}>
                          <input
                            type="checkbox"
                            checked={selected.includes(option.value)}
                            onChange={() => toggleRecsDraftFilter(group.key, option.value)}
                          />
                          <span className={styles.fpOptLbl}>{option.label}</span>
                          <span className={styles.fpOptCount}>{option.count}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.fpFoot}>
              <button type="button" className={styles.fpReset} onClick={resetRecsDraftFilters}>
                Reset All
              </button>
              <button type="button" className={styles.fpApply} onClick={applyRecsFilters}>
                Apply Filters
              </button>
            </div>
          </div>

          {/* TAB: AI Chat */}
          {activeTab === "chat" && (
            <section className={styles.aiChatSection}>
              <div className={styles.aiChatShell}>
                <aside className={styles.aiChatSidebar}>
                  <div className={styles.aiChatSidebarHeader}>
                    <div>
                      <h3>Threads</h3>
                      <p>Revisit past conversations</p>
                    </div>
                    <button
                      type="button"
                      className={styles.aiChatNewThread}
                      onClick={onCreateNewChatThread}
                      disabled={chatInitializing || chatTyping}
                    >
                      New
                    </button>
                  </div>

                  <div className={styles.aiChatThreadList}>
                    {chatThreads.length === 0 && (
                      <div className={styles.aiChatThreadEmpty}>No previous threads yet.</div>
                    )}
                    {chatThreads.map((thread, index) => {
                      const threadId = thread?.insightChatThreadId;
                      const active = threadId && threadId === chatThreadId;

                      return (
                        <button
                          key={threadId || `chat-thread-${index}`}
                          type="button"
                          className={
                            active
                              ? `${styles.aiChatThreadItem} ${styles.aiChatThreadItemActive}`
                              : styles.aiChatThreadItem
                          }
                          onClick={() => onSelectChatThread(threadId)}
                          disabled={!threadId || chatInitializing || chatTyping}
                        >
                          <div className={styles.aiChatThreadTitle}>{thread.displayLabel}</div>
                          {thread.displayTime && (
                            <div className={styles.aiChatThreadMeta}>{thread.displayTime}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </aside>

                <div className={styles.aiChatMain}>
                  <div className={styles.aiChatHeader}>
                    <div>
                      <h3>AI Campaign Assistant</h3>
                      <p>Ask about campaign insights, concerns, and suggested actions.</p>
                    </div>
                  </div>

                  <div className={styles.aiChatMessages}>
                    {chatMessages.map((message) => (
                      <div
                        key={message.id}
                        className={
                          message.role === "user"
                            ? styles.aiChatBubbleUser
                            : styles.aiChatBubbleAssistant
                        }
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatChatMessageHtml(message.text),
                          }}
                        />
                      </div>
                    ))}
                    {chatTyping && (
                      <div className={styles.aiChatTyping}>Assistant is typing...</div>
                    )}
                    {chatInitializing && (
                      <div className={styles.aiChatTyping}>Loading conversation...</div>
                    )}
                  </div>

                  <div className={styles.aiChatInputRow}>
                    <input
                      className={styles.aiChatInput}
                      placeholder="Ask: Give me a quick summary"
                      value={chatInput}
                      onChange={(event) => setChatInput(event.target.value)}
                      disabled={chatTyping || chatInitializing}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !chatTyping && !chatInitializing) {
                          event.preventDefault();
                          onSendChat();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={styles.aiChatSend}
                      onClick={onSendChat}
                      disabled={!chatInput.trim() || chatTyping || chatInitializing}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {selectedRecording && (
          <div
            className={styles.rdOverlay}
            role="presentation"
            onMouseDown={() => setSelectedRecording(null)}
          >
            <div
              className={styles.rdBox}
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.rdClose}
                onClick={() => setSelectedRecording(null)}
              >
                ×
              </button>
              <div className={styles.rdContent}>
                {/* Header */}
                <h2 className={styles.rdName}>
                  {selectedRecording.respondentName ||
                    selectedRecording.enumerator ||
                    "Recording Detail"}
                </h2>

                {/* Survey Information */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Survey Information</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Location</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.location}
                        {selectedRecording.barangay && (
                          <>
                            <br />
                            <small style={{ color: "rgba(191,219,254,.55)" }}>
                              {selectedRecording.barangay}
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Campaign</div>
                      <div className={styles.rdfv}>
                        <span className={styles.badgeBlue}>{selectedRecording.campaignCode}</span>
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Date &amp; Time</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.dateRaw
                          ? new Date(selectedRecording.dateRaw).toLocaleString()
                          : [selectedRecording.date, selectedRecording.time]
                              .filter(Boolean)
                              .join(", ") || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Enumerator</div>
                      <div className={styles.rdfv}>{selectedRecording.enumerator}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Sentiment</div>
                      <div className={styles.rdfv}>
                        <span
                          className={styles.recSentBadge}
                          style={{
                            borderColor: `${sentimentColor(selectedRecording.sentiment)}55`,
                            background: `${sentimentColor(selectedRecording.sentiment)}22`,
                            color: sentimentColor(selectedRecording.sentiment),
                            textTransform: "uppercase",
                          }}
                        >
                          {selectedRecording.sentiment}
                        </span>
                      </div>
                    </div>
                    {selectedRecording.tone && (
                      <div className={styles.rdf}>
                        <div className={styles.rdfl}>Tone</div>
                        <div className={styles.rdfv}>{selectedRecording.tone}</div>
                      </div>
                    )}
                  </div>
                  {selectedRecording.concernChips.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                        Key Concerns
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {selectedRecording.concernChips.map((chip) => (
                          <span key={chip} className={styles.badgePurple}>
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Audio */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Audio Recording</div>
                  <div className={styles.audioPl}>
                    {selectedRecording.audioUrl && (
                      <audio
                        ref={audioRef}
                        src={selectedRecording.audioUrl}
                        onPlay={() => setAudioPlaying(true)}
                        onPause={() => setAudioPlaying(false)}
                        onEnded={() => {
                          setAudioPlaying(false);
                          setAudioCurrent(0);
                        }}
                        onTimeUpdate={(e) => setAudioCurrent(e.currentTarget.currentTime)}
                        onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                        style={{ display: "none" }}
                      />
                    )}
                    <button
                      type="button"
                      className={styles.playBtn}
                      onClick={togglePlay}
                      disabled={!selectedRecording.audioUrl}
                      aria-label={audioPlaying ? "Pause" : "Play"}
                    >
                      {audioPlaying ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <rect x="5" y="3" width="4" height="18" rx="1" />
                          <rect x="15" y="3" width="4" height="18" rx="1" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      )}
                    </button>
                    <div
                      className={styles.progBar}
                      onClick={seekAudio}
                      role="progressbar"
                      aria-valuenow={audioCurrent}
                      aria-valuemax={audioDuration}
                    >
                      <div
                        className={styles.progFill}
                        style={{
                          width: audioDuration ? `${(audioCurrent / audioDuration) * 100}%` : "0%",
                        }}
                      />
                    </div>
                    <div className={styles.dur}>
                      {formatAudioTime(audioCurrent)} / {formatAudioTime(audioDuration)}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {selectedRecording.summary && (
                  <div className={styles.rdSec}>
                    <div className={styles.rdSecH}>AI-Generated Summary</div>
                    <div className={styles.aiBox}>
                      <p>{selectedRecording.summary}</p>
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {selectedRecording.transcript && (
                  <div className={styles.rdSec}>
                    <div className={styles.rdSecH}>Full Transcript</div>
                    <div className={styles.txBox}>
                      {selectedRecording.transcript
                        .split("\n")
                        .filter(Boolean)
                        .map((line, i) => {
                          const speakerMatch = line.match(/^(speaker_\w+|interviewer|int)\s*:?/i);
                          const isQ = line.match(/^(interviewer|int(erviewer)?)\s*:?/i);
                          const label = speakerMatch
                            ? speakerMatch[0].replace(/:$/, "").trim()
                            : null;
                          const rest = label ? line.slice(speakerMatch[0].length).trim() : line;
                          return (
                            <div
                              key={i}
                              className={`${styles.txLine} ${isQ ? styles.txQ : styles.txA}`}
                            >
                              {label && <span className={styles.txSpeaker}>{label}: </span>}
                              {rest}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Basic Identity */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Basic Identity</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Age Group</div>
                      <div className={styles.rdfv}>{selectedRecording.ageGroup || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Gender</div>
                      <div className={styles.rdfv}>{selectedRecording.sex || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Civil Status</div>
                      <div className={styles.rdfv}>{selectedRecording.civilStatus || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Dependents</div>
                      <div className={styles.rdfv}>{selectedRecording.dependents || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Residency & Socioeconomic */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Residency &amp; Socioeconomic</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Years in Area</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.yearsInArea || selectedRecording.residency || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Area Type (Urban/Rural)</div>
                      <div className={styles.rdfv}>{selectedRecording.areaType || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Social Class</div>
                      <div className={styles.rdfv}>{selectedRecording.socialClass || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Employment Sector</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.employmentSector || selectedRecording.occupation || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Monthly Income</div>
                      <div className={styles.rdfv}>{selectedRecording.monthlyIncome || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Housing Type</div>
                      <div className={styles.rdfv}>{selectedRecording.housing || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Media & Information */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Media &amp; Information</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Primary Platform</div>
                      <div className={styles.rdfv}>{selectedRecording.primaryPlatform || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Religion & Culture */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Religion &amp; Culture</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Religion</div>
                      <div className={styles.rdfv}>{selectedRecording.religion || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Ethnicity / Language</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.ethnicityLanguage || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Civic & Political */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Civic &amp; Political</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Voter Status</div>
                      <div className={styles.rdfv}>{selectedRecording.voterStatus || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Vote Intent</div>
                      <div className={styles.rdfv}>{selectedRecording.voteIntent || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Vote Intent Strength</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.voteIntentStrength || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>National Gov Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.nationalGov || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Local Gov - Governor Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.governor || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Local Gov - Mayor Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.mayor || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Leader Preferences */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Leader Preferences</div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl}>
                      Core Values:{" "}
                      <span style={{ color: "#b0bcd4" }}>
                        {selectedRecording.coreValues || "-"}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl}>
                      Leader Archetype Preference:{" "}
                      <span style={{ color: "#b0bcd4" }}>
                        {selectedRecording.leaderArchetype || "-"}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                      Desired Traits
                    </div>
                    {selectedRecording.desiredTraits.length > 0 ? (
                      <div className={styles.traitRow}>
                        {selectedRecording.desiredTraits.map((t) => (
                          <span key={t} className={styles.traitD}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.rdfv}>-</div>
                    )}
                  </div>
                  <div>
                    <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                      Rejected Traits
                    </div>
                    {selectedRecording.rejectedTraits.length > 0 ? (
                      <div className={styles.traitRow}>
                        {selectedRecording.rejectedTraits.map((t) => (
                          <span key={t} className={styles.traitR}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.rdfv}>-</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isDeleteRecordingConfirmOpen && recordingToDelete && (
          <div
            className={styles.deleteRecordingOverlay}
            role="presentation"
            onMouseDown={onCloseDeleteRecordingModal}
          >
            <div
              className={styles.deleteRecordingModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-recording-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={styles.deleteRecordingHeader}>
                <div className={styles.deleteRecordingIconWrap} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h3 id="delete-recording-title" className={styles.deleteRecordingTitle}>
                    Delete Recording
                  </h3>
                  <p className={styles.deleteRecordingMessage}>
                    You are about to delete recording{" "}
                    {formatRecordIdValue(recordingToDelete.fieldResponseProcessedId)}.
                  </p>
                </div>
              </div>

              <div className={styles.deleteRecordingWarningBox}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <circle cx="12" cy="16.5" r="1" />
                </svg>
                <p>
                  This will remove this recording from any insights or reports generated for this
                  campaign.
                </p>
              </div>

              <div className={styles.deleteRecordingActions}>
                <button
                  type="button"
                  className={styles.deleteRecordingCancelButton}
                  onClick={onCloseDeleteRecordingModal}
                  disabled={isDeletingRecording}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.deleteRecordingConfirmButton}
                  onClick={onConfirmDeleteRecording}
                  disabled={isDeletingRecording}
                >
                  {isDeletingRecording ? "Deleting..." : "Delete Recording"}
                </button>
              </div>
              {deleteRecordingError && (
                <p className={styles.deleteRecordingError}>{deleteRecordingError}</p>
              )}
            </div>
          </div>
        )}

        {isDeleteRecordingSuccessOpen && (
          <div
            className={styles.deleteRecordingOverlay}
            role="presentation"
            onMouseDown={onCloseDeleteRecordingSuccessModal}
          >
            <div
              className={styles.deleteRecordingSuccessModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-recording-success-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={styles.deleteRecordingSuccessIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3
                id="delete-recording-success-title"
                className={styles.deleteRecordingSuccessTitle}
              >
                Recording Deleted
              </h3>
              <p className={styles.deleteRecordingSuccessMessage}>
                The recording has been successfully removed from this campaign.
              </p>

              <button
                type="button"
                className={styles.deleteRecordingDoneButton}
                onClick={onCloseDeleteRecordingSuccessModal}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {endCampaignModal}
      </div>
    );
  }

  /* ── LIST VIEW ── */
  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h1>My Campaigns</h1>
          <p>Select a campaign to view its detailed data, or create a new one</p>
        </div>
        <button className={styles.createButton} type="button" onClick={onOpenCreateModal}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Campaign
        </button>
      </div>

      <section className={styles.statsGrid}>
        {statCards.map((card) => (
          <article key={card.label} className={styles.statCard}>
            <p>{card.label}</p>
            <strong className={styles[card.tone]}>
              {card.label === "Total Campaigns"
                ? campaigns.length
                : card.label === "Active"
                  ? campaigns.filter((campaign) => campaign.status === "active").length
                  : card.label === "Total Recordings"
                    ? totalRecordingsFromApi
                    : card.label === "Enumerators"
                      ? totalEnumeratorsFromList
                      : card.value}
            </strong>
          </article>
        ))}
      </section>

      {campaignsLoading && <div className={styles.analyticsLoader}>Loading campaigns...</div>}
      {campaignsError && <div className={styles.analyticsError}>{campaignsError}</div>}

      <section className={styles.campaignList}>
        {campaigns.map((campaign) => (
          <article key={campaign.code} className={styles.campaignCard}>
            <div className={styles.campHd}>
              <div>
                <div className={styles.identityRow}>
                  <span className={styles.code}>{campaign.code}</span>
                  <span className={styles.status}>{campaign.status}</span>
                </div>
                <h3 className={styles.campTitle}>{campaign.title}</h3>
                <p className={styles.campSub}>
                  {campaign.location}&nbsp;&nbsp;·&nbsp;&nbsp;{campaign.date}
                </p>
              </div>
              <div className={styles.campActs}>
                <button
                  type="button"
                  className={styles.endButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenEndCampaignModal(campaign);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <rect x="9" y="9" width="6" height="6" />
                  </svg>
                  End
                </button>
                <button
                  type="button"
                  className={styles.openButton}
                  onClick={() => openCampaign(campaign)}
                >
                  Open Campaign
                  <svg
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
            {(() => {
              const recCount = campaignRecordingTotals[campaign.code] ?? 0;
              const demog = campaignListDemographics[campaign.code];
              const sexEntries = resolveDistributionEntries(demog?.sex);
              const ageEntries = resolveAgeDistributionEntries(demog?.age)
                .sort((a, b) => b.count - a.count)
                .slice(0, 4);
              const maleCount =
                sexEntries.find((e) => String(e.label).toLowerCase().startsWith("m"))?.count ?? 0;
              const femaleCount =
                sexEntries.find((e) => String(e.label).toLowerCase().startsWith("f"))?.count ?? 0;
              const genderTotal = maleCount + femaleCount || 1;
              const circumference = 87.96;
              const maleDash = ((maleCount / genderTotal) * circumference).toFixed(1);
              const ageMax = ageEntries.reduce((m, e) => Math.max(m, e.count), 1);
              return (
                <div className={styles.campBody}>
                  <div className={styles.campCounts}>
                    <div>
                      <div className={styles.cmetaLbl}>Recordings</div>
                      <div className={styles.cmetaBigVal}>{recCount}</div>
                    </div>
                    <div>
                      <div className={styles.cmetaLbl}>Enumerators</div>
                      <div className={styles.cmetaBigVal}>
                        {campaignListEnumeratorCounts[campaign.code] ?? campaign.enumerators ?? 0}
                      </div>
                    </div>
                  </div>
                  <div className={styles.campDemog}>
                    <div className={styles.cmetaLbl} style={{ marginBottom: 6 }}>
                      Respondent Demographics
                    </div>
                    <div className={styles.campDemogGrid}>
                      <div className={styles.campGenderRow}>
                        <svg width="44" height="44" viewBox="0 0 44 44" style={{ flexShrink: 0 }}>
                          <circle
                            cx="22"
                            cy="22"
                            r="14"
                            fill="none"
                            stroke="#f472b6"
                            strokeWidth="9"
                          />
                          <circle
                            cx="22"
                            cy="22"
                            r="14"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="9"
                            strokeDasharray={`${maleDash} ${circumference}`}
                            transform="rotate(-90 22 22)"
                          />
                          <circle cx="22" cy="22" r="9" fill="#111827" />
                        </svg>
                        <div>
                          <div className={styles.cmetaLbl} style={{ marginBottom: 4 }}>
                            Gender
                          </div>
                          <div className={styles.genderLegend}>
                            <span className={styles.gLegMale}>
                              <span className={styles.gDotMale} />
                              {maleCount} Male
                            </span>
                            <span className={styles.gLegFemale}>
                              <span className={styles.gDotFemale} />
                              {femaleCount} Female
                            </span>
                          </div>
                        </div>
                      </div>
                      {ageEntries.length > 0 && (
                        <div>
                          <div className={styles.cmetaLbl} style={{ marginBottom: 5 }}>
                            Age groups
                          </div>
                          {ageEntries.map((e) => (
                            <div key={e.label} className={styles.ageBarRow}>
                              <div className={styles.ageBarLbl}>{e.label}</div>
                              <div className={styles.ageBarTrack}>
                                <div
                                  className={styles.ageBarFill}
                                  style={{ width: `${(e.count / ageMax) * 100}%` }}
                                />
                              </div>
                              <div className={styles.ageBarCount}>{e.count}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </article>
        ))}
      </section>

      {endCampaignModal}
      {createModal}
    </div>
  );
};

export default CampaignsPage;
